/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W4 — PLATE HEAT EXCHANGER SELECTOR (§33.4).
 *
 * React port of components/plate_heat_exchanger/view/plate_heat_exchanger
 * .selector.js (3,275) + its templates into the 6e2c component-selector chassis
 * (§30) as the 'plate_heat_exchanger' tab body. Closely mirrors the W3
 * EnergyWheelSelector (same dependency-graph + summer/winter shape) — REUSES the
 * W3 engines heavily: useRatingDispatch, buildRatingEnvelope (PHX_MFG_MAPPING),
 * useDependencyGraph (the cascading-reset + last-write-wins reducer), the
 * RatedOutputsTable/ratingKit, getFromCmsData (via phxCms).
 *
 * Opened via the PHX FORM's rating label: PlateHeatExchangerForm's FormHeading
 * (withRatingBlock, ratingBlockDataName='plate_heat_exchanger') → openRatingStub
 * → rating_mode:activate {name:'plate_heat_exchanger', current_model:{id},
 * section, rated_phx_model?}. The §30.4 fan-out key is therefore
 * `plate_heat_exchanger_<id>:selector:<action>`.
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT):
 *   - RATE fires an EXTERNAL vendor call (POST /rating/ → Recuperator/Heatex).
 *     SELECT writes: current_model.set → auto-save PATCH ahu/plate_heat_exchangers
 *     /<id> + self.model.set(rating_fields) → PATCH ahu/plate_heat_exchangers/
 *     rating_mode/<id> (patchRatedPhx) + the master_phx_id.resetFilter reach.
 *     BOTH are armed ONLY by a real #rate/#select click (userArmed ref). NOTHING
 *     fires on mount/open/tab-switch/browse.
 *   - SMOKE = open / render / cancel → 0 mutating, 0 /rating/ calls.
 *   - AHU 1474 is the ONLY rated PHX on dev3 — the read-only render fixture; we
 *     render the STORED rated state read-only and NEVER write to it during smoke.
 *
 * THE DEPENDENCY ENGINE (the W3/W4 deliverable): useDependencyGraph runs the
 * legacy DependenciesHandler cascade + the VersionedDataRegistry last-write-wins.
 * Option lists resolve through:
 *   - the phx CMS doc (flow_type/spacing/face/bypass/recirc — phxCms, which
 *     reuses getFromCmsData), and
 *   - the master param lists (everything else — phxParamUrl / master fetches).
 * Read-only, the saved value always renders (the option list is the full CMS
 * superset / the saved-label fallback). The live re-cascade fires only when the
 * user changes a parent — and because Rate/Select are userArmed, the read-only
 * smoke never mutates.
 */

import {
  Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  RATING_STORAGE_KEYS,
  readRatingMap,
  findLastEntry,
  findEntryByMfg,
  writeRatingEntry,
  type IndependentRatingEntry,
  type IndependentRatingMap,
} from 'pages/dashboard/components/header/independentRatingStorage';
import { optionLabel, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import { useAppDispatch } from 'app/hooks';
import {
  ahuComponentFormsApi,
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  useGetMasterListByUrlQuery,
  useGetTypeCertificationsQuery,
  useCreateTypeCertificationMutation,
  useDeleteTypeCertificationMutation,
  usePatchRatedPhxMutation,
} from 'app/services/ahuComponentFormsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAhuBoardProjectQuery } from 'app/services/ahuBoardApi';
import {
  PlateHeatExchangerRatedOutputs,
  type PhxOutputTab,
} from './PlateHeatExchangerRatedOutputs';
import { useRatingDispatch } from './useRatingDispatch';
import { buildRatingEnvelope } from './ratingEnvelope';
import { useDependencyGraph, type LoadParamsFn } from './useDependencyGraph';
import {
  PHX_CMS_DOC_URL,
  PHX_NOTES_URL,
  PHX_AIR_STREAMS_URL,
  PHX_DEPENDENCY_GRAPH,
  PHX_REQUIRED_FIELDS,
  PHX_DEFAULTS,
  PHX_PARAMETER_NAMES,
  PHX_FIELDS_TO_NULLIFY,
  SUMMER_REQUIRED_FIELDS_FOR_RATE,
  WINTER_REQUIRED_FIELDS_FOR_RATE,
  phxParamUrl,
  phxSizesUrl,
  PHX_MODULE_CONFIGS_URL,
  PHX_MFG_SETTINGS_URL,
  PHX_CATALOG_BASE_URL,
  PHX_PRICING_METHODS_URL,
  PHX_SHAFT_SHAPE_URL,
  PHX_SHAFT_LENGTH_URL,
  PHX_SHAFT_DIAMETER_URL,
} from './phxSelectorData';
import {
  phxCmsOptionList,
  phxCmsSelectedLabel,
  phxCmsSelectedValue,
  isPhxCmsField,
  type PhxCmsDoc,
} from './phxCms';
import { useGetPhxNotesQuery } from './phxSelectorApi';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import { guardSelect } from '../cascadeCommit';
import { pendingSelectWrites, selectWriteKey, trackSelectWrites } from './selectWriteGate';
import { disableSyncIfAirFieldEdited } from '../commitAirConditions';
import { takeComponentPendingRerate } from '../../psychrometric/pendingRerate';
import { generateRatingTimestamp } from './ratingTimestamp';

const num = (v: string): number | null => {
  if (v === '' || v === 'None' || v === 'none' || v === '--') return null;
  const n = Number(v.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

type SeasonTab = 'summer' | 'winter';
// ★ JOB A — restore the FULL legacy top-level tab set
// (plate_heat_exchanger.selector.tmpl:7-21): Properties ("Heat-Exchanger"),
// Performance, Bypass (hidden for config 1/3), Settings, Pricing. The React port
// had flattened this to 3 (Properties/Performance/Pricing) and folded Settings +
// part of Bypass into Properties.
type InputTab = 'Properties' | 'Performance' | 'Bypass' | 'Settings' | 'Pricing';
// Properties sub-tabs (tmpl:31-44) and Bypass sub-tabs (tmpl:477-487).
type PropSubTab = 'general' | 'internal_plates' | 'end_plates' | 'corner_profiles';
type BypassSubTab = 'general' | 'bypass' | 'recirculation';

interface PhxModel extends Record<string, unknown> {
  id?: number;
  /** the saved vendor model NAME (`AHUPHX.model`) — legacy's "is there a stored
   *  rating to draw" flag, see the seed effect. */
  model?: string | null;
  master_phx_id?: number | null;
  plate_heat_exchanger_params?: string | null;
  master_phx?: Record<string, unknown>;
  generic_mfg?: boolean;
}

/**
 * ★ Phase 6.5 — the HEADER-mode seed defaults.
 *
 * Legacy synthesises the missing `current_model` from `self.default_values`
 * (plate_heat_exchanger.selector.js:215-364) — `new
 * Backbone.Model(self.default_values)` at :432 — and the missing rated model from
 * `self.rated_default_values` (:365-390) at :1450. In header mode it ALSO seeds
 * `self.dataForRate = {...self.rated_default_values}` (:430).
 *
 * `PHX_DEFAULTS` already carries every default_values member the React flat state
 * renders; the three added below come from `rated_default_values` / the
 * `master_phx` sub-object which React's board seed normally reads off `detail`:
 *   mfg_id 52 (Heatex) · series_id 276 (H) · flow_type_id 1 (Crossflow).
 * `dual_tunnel_air_rel_dirs` is carried too because legacy feeds it to
 * `dataForRating` at :954 (unlike `tunnel_configuration_value` at :956, which is
 * gated on `self.ahu` and therefore ABSENT in header mode).
 */
const HEADER_PHX_DEFAULTS: Record<string, unknown> = {
  ...PHX_DEFAULTS,
  mfg_id: 52,
  series_id: 276,
  flow_type_id: 1,
  dual_tunnel_air_rel_dirs: 'parallel',
};

/**
 * ★ RESET (#reset, the circular-arrow icon in the rating-dialog header) —
 * `self.rated_default_values` (plate_heat_exchanger.selector.js:365-390).
 *
 * DISTINCT from `self.default_values` (:216-364, ported as PHX_DEFAULTS): that
 * one seeds a brand-new PHX's MODEL fields, this one is the catalogue/rating
 * half (mfg/series/size/shape/flow_type plus the vendor-returned geometry), and
 * it is the ONLY half `reset` (:1846-1884) restores.
 *
 * ★ `max_allowed_dp` is deliberately absent here. Legacy grafts it onto
 * `rated_default_values` at :880 (`self.rated_default_values.max_allowed_dp =
 * mfgSettings.max_allowed_apd`) once GET mfg-settings resolves, so a reset that
 * happens after that load also restores the cap. doReset adds it from the
 * `maxAllowedApd` mirror ref instead of mutating this frozen literal.
 */
const PHX_RATED_DEFAULT_VALUES: Record<string, unknown> = {
  mfg_id: 52,
  series_id: 276,
  size_id: null,
  shape_id: null,
  flow_type_id: 1,
  plate_spacing_code_id: null,
  plate_spacing: null,
  api_dll_version: null,
  plate_thickness: null,
  casing_height: null,
  casing_depth: null,
  airway_height: null,
  mfg: 'Heatex',
  series: 'H',
  size: null,
  shape: null,
  flow_type: 'Crossflow',
  casing_height_mm: null,
  // NB `plate_spacing_code` is commented out in legacy (:378) — not carried.
  model: null,
  phx_mfg: 'Heatex',
  shipping_weight: null,
  operating_weight: null,
};

/**
 * The `<select class="search_field">` NAMES that `reset` blanks to null
 * (js:1852-1858 — `self.$el.find('.search_field').each(…)`, blanking only
 * `elem.tagName == 'SELECT'`).
 *
 * ★★ WHY A TRANSCRIBED LITERAL AND NOT A DOM QUERY. In the LEGACY template
 * `.search_field` is a real discriminator: exactly these eleven selects carry it
 * (plate_heat_exchanger.selector.tmpl:64, 81, 85, 98, 113, 127, 142, 205, 524,
 * 568, 584); every other select there is plain `model_field`. The React port,
 * however, hardcodes `className="search_field"` on EVERY select it renders
 * (DocSelect / MasterSelect / OptionSelect all do), so querying this dialog's
 * own DOM for `.search_field` would blank ~30 fields where legacy blanks 11.
 * The legacy template is therefore the source of truth for the list.
 *
 * ★ `total_width_max` / `total_height_max` also carry `.search_field`
 * (tmpl:174/191) but are `<input>`, and the legacy loop skips non-SELECTs — so
 * they SURVIVE a reset. ★ `shape_id` is `class="hidden search_field"`
 * (tmpl:85) and the React port renders no select for it at all, but it is in
 * the blanked set (and lands null again from rated_default_values anyway).
 */
const PHX_RESET_BLANKED_SELECTS = [
  'mfg_id',
  'series_id',
  'shape_id',
  'size_id',
  'module_configuration_id',
  'flow_type_id',
  'temp_rating_id',
  'mounting_id',
  'bypass_location_id',
  'bypass_side_id',
  'bypass_dmr_config_id',
];

/**
 * `updateDefaultsDependsOnMFG` (js:2759-2808) — the per-manufacturer MODEL-field
 * defaults that `changeSearchField`'s `mfg_id` branch applies (js:2487).
 *
 * Reached from doReset because legacy's `reset` ends in
 * `$select.trigger('change')` on the mfg select, which runs that branch — so
 * these land ON TOP of the reset blob. Note 167 (Recuperator) overrides the
 * blob's `configuration_id: 3` pin with 2, which is what re-shows the Bypass
 * tab (toggleBypassVisibility hides it for config 1/3, js:2742-2757).
 *
 * ★ SCOPE: only doReset applies this. React's `onSearchField('mfg_id')` does NOT
 * (pre-existing gap in the port) — left alone deliberately, since touching it
 * would change the behaviour of a path that works today.
 */
const PHX_MFG_DEFAULTS: Record<number, Record<string, unknown>> = {
  167: {
    configuration_id: 2,
    calc_mode_id: 1,
    casing_height: null,
    end_plate_finish_id: 1,
    corner_profile_style_id: 1,
    end_plate_mtl_id: null,
    end_plate_style_id: null,
    mounting_id: null,
    seal_id: null,
    plate_finish_id: null,
    corner_profile_finish_id: null,
  },
  52: {
    configuration_id: 3,
    mounting_id: 1,
    seal_id: 8,
    calc_mode_id: 4,
    plate_finish_id: 1,
    end_plate_finish_id: 1,
    end_plate_mtl_id: 4996,
    end_plate_style_id: 5,
    corner_profile_style_id: 4,
    corner_profile_finish_id: 1,
    casing_height: null,
  },
};

/**
 * ★ FOCUS PARITY — why every sub-view of this selector lives at MODULE scope.
 *
 * `Num`, `PairNum`, `SeasonBlock`, `DocSelect`, `MasterSelect` … were all
 * declared INSIDE the selector's render, so each render produced a NEW function
 * identity. React reconciles on element TYPE, so it saw a different type every
 * time and unmounted/remounted every `<input>` and `<select>` in the dialog on
 * any state change.
 *
 * That is what made the number boxes untypable. Every keystroke commits
 * (`onChange` → `onModelField`/`onSearchField` → `bump()`), the re-render that
 * follows DESTROYS the node the caret is in, and focus falls back to `<body>` —
 * so the first character lands, the rest go nowhere. Reported against the
 * Performance tab (Airflow / DB Temperature / Humidity), but it applied to every
 * field in the dialog; changing a `<select>` dropped focus the same way.
 *
 * Legacy cannot hit this: the pane is one Handlebars render and
 * `formatter.js:189-193` only rewrites the element's own `.value` on change, so
 * the node the browser has focused is still there. Hoisting these out restores
 * that — React now updates the existing DOM nodes in place and focus survives.
 *
 * This is the same defect, and the same fix, as the energy-wheel selector's
 * (see the matching note in `EnergyWheelSelector.tsx`); the PHX file never got
 * the treatment.
 *
 * They close over a lot of render state, so it travels through this context
 * rather than ~40 threaded props. A changing context VALUE re-renders consumers,
 * which is harmless; only a changing component IDENTITY remounts them.
 */
type PhxViewCtx = {
  s: Record<string, unknown>;
  season: SeasonTab;
  isHeatex: boolean;
  streams: OptionRow[];
  masterLists: Record<string, OptionRow[]>;
  phxDoc: unknown;
  detail: unknown;
  masterPhx: Record<string, unknown>;
  projectElevation: string;
  pmId: number | null;
  selectedCerts: Set<number>;
  certListOpen: boolean;
  setCertListOpen: (fn: (open: boolean) => boolean) => void;
  toggleCertification: (certId: number, checked: boolean) => void;
  onSearchField: (name: string, raw: string) => void;
  onModelField: (name: string, raw: string | null) => void;
};
const PhxCtx = createContext<PhxViewCtx | null>(null);
const usePhx = (): PhxViewCtx => {
  const ctx = useContext(PhxCtx);
  if (!ctx) throw new Error('PlateHeatExchangerSelector sub-view rendered outside PhxCtx');
  return ctx;
};

export const PlateHeatExchangerSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
  headerMode = false,
  eventScope,
  bus: busProp,
}: SelectorTabProps) => {
  // the chassis fan-out prefix = the name we were opened under.
  const eventName = (activate.name as string) || 'plate_heat_exchanger';
  // §30.4 fan-out namespace: `plate_heat_exchanger_<id>` on the board,
  // `plate_heat_exchanger_header` in the header popup — legacy resolves the same
  // fallback at plate_heat_exchanger.selector.js:849
  // `self.model.id ? self.model.id : "header"` (the SYNTHESIZED rated model has
  // no `id`).
  const scope = eventScope ?? String(modelId);
  // Isolated bus in header mode so the UN-namespaced emits below
  // (rating_mode:mounting:changed, cancel:dialog:changes …) can never reach a
  // board rating dialog open on the same page — discovery §7.3 trap 1.
  const bus = busProp ?? ahuBoardBus;
  // Own-subtree root for every DOM read. Legacy reaches `#select`/`#print`/
  // `#notes` with `self.$el.parents('.ahu-rate-content').find(...)` for some and
  // DOCUMENT-WIDE selectors for others (plate_heat_exchanger.selector.js:2914,
  // 2918 `$('#component_rating_mode .control-buttons button[id="select"]')`) —
  // on a page hosting BOTH a header popup and a board dialog those
  // cross-contaminate. Discovery §7.3 trap 1.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const chassisEl = useCallback(
    (): HTMLElement | null => rootRef.current?.closest('.ahu-rate-content') ?? null,
    []
  );
  // plate_heat_exchanger.selector.tmpl:27 `id="{{prefix}}Properties"` with
  // `prefix: self.header ? "" : "ahu-"` (js:1511 / :2475), so the pane ids drop
  // the prefix in header mode and cannot collide with a board dialog's panes.
  // NOTE the existing board ids use an UNDERSCORE (`ahu_Properties`) rather than
  // legacy's hyphen — a pre-existing 6e2c deviation left untouched.
  const idPrefix = headerMode ? '' : 'ahu_';

  // ---- read-only loads (fire on mount; never mutate) ----
  // ★ HEADER MODE: there is no current_model to read — legacy SYNTHESIZES one
  // from `self.default_values` (js:428-449) and explicitly SKIPS the fetch:
  // `!self.header ? self.current_model.fetch() : null` (js:811). `modelId` is 0
  // there; `skip` mirrors that exactly.
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    fulfilledTimeStamp: detailFulfilledAt,
  } = useGetComponentDetailQuery(
    { plural: 'plate_heat_exchangers', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );

  // ---- ★ seed only from a row that POSTDATES this open (the reopen gate) ----
  // Second half of the "reopen shows an empty Output pane" report. The stored
  // rated sheet is seeded below off the detail GET, and `getComponentDetail`
  // carries no tags: RTK Query keeps the unsubscribed entry for its default
  // 60 s while the chassis mounts a FRESH selector on every open
  // (`key={dialog.openSeq}`). So a reopen inside that minute is served the
  // CACHED — pre-Select — row synchronously. `refetchOnMountOrArgChange` does
  // re-pull, but `isLoading` is false the whole time (true only on the very
  // first ever load) and the seed latches `seeded.current = modelId`, which
  // blocks the re-seed when the fresh row lands.
  //
  // Legacy cannot have this: `render_outputs` reads `self.current_model`
  // (plate_heat_exchanger.selector.js:1895), the in-memory Backbone model that
  // Select already `.set()` on the way past — no re-read, nothing to be stale.
  //
  // Same fix as the coil (CoilSelector), the energy wheel and the gas heater.
  // ⚠ `isFetching` is not a usable gate: RTK Query starts the refetch from an
  // effect inside the hook, so the first render after mount still reports
  // `isFetching: false` next to the stale `data` — the seed effect runs in that
  // very commit. `fulfilledTimeStamp` is stamped when a request FULFILS, so a
  // row cached before this dialog opened can never satisfy `>= openedAt`.
  const openedAt = useRef(Date.now());
  // …and a GET issued while the previous Select is still saving would fulfil
  // after `openedAt` while STILL returning the pre-Select row — the dialog
  // closes on the Select CLICK (finishSelect runs before the POST/PATCH
  // resolve), so that window is exactly the one the reporter clicks in. When
  // this open follows a Select whose chain is in flight, hold the gate open
  // (`Infinity`) until those writes settle, then re-read and gate on THAT
  // moment.
  const pendingWrites = useRef(
    pendingSelectWrites(selectWriteKey('plate_heat_exchangers', modelId))
  );
  const [gateAt, setGateAt] = useState<number>(() =>
    pendingWrites.current ? Number.POSITIVE_INFINITY : openedAt.current
  );
  const dispatch = useAppDispatch();
  useEffect(() => {
    const writes = pendingWrites.current;
    if (!writes) return;
    let alive = true;
    void writes.then(() => {
      if (!alive) return;
      // stamped BEFORE the refetch is dispatched, so only its (later)
      // fulfilment can satisfy the gate.
      const at = Date.now();
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'plate_heat_exchangers', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      setGateAt(at);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);
  // `isError` is the escape hatch: a failed read seeds from whatever is cached
  // rather than pinning the preloader on for ever.
  const detailFresh = detailError || (detailFulfilledAt != null && detailFulfilledAt >= gateAt);

  // the phx CMS params doc (flow_type/spacing/face/bypass/recirc labels).
  const { data: phxDoc, isLoading: phxDocLoading } =
    useGetComponentCmsDocQuery(PHX_CMS_DOC_URL);

  const airStreamsQ = useGetMasterListByUrlQuery(PHX_AIR_STREAMS_URL);
  // damper-shaft master lists (legacy DamperShaft.Shape/Diameter/Length, js:207-209)
  // — read ONLY by the Bypass output tab, which also carries the `data-id` that
  // Select lifts back off the DOM (js:1909-1911).
  const shaftShapesQ = useGetMasterListByUrlQuery(PHX_SHAFT_SHAPE_URL);
  const shaftLengthsQ = useGetMasterListByUrlQuery(PHX_SHAFT_LENGTH_URL);
  const shaftDiametersQ = useGetMasterListByUrlQuery(PHX_SHAFT_DIAMETER_URL);
  // Selector parity — the REAL pricing_methods collection (was hardcoded List/User;
  // legacy self.pricing_methods, js:206/1569). Falls back to List/User if the
  // endpoint returns nothing, so a method beyond those exposes the price_* fields.
  const pricingMethodsQ = useGetMasterListByUrlQuery(PHX_PRICING_METHODS_URL);
  const notesQ = useGetPhxNotesQuery(PHX_NOTES_URL);
  // certifications relation (multiCheckbox ahuphxcertification) — read-only here.
  // ★ HEADER MODE: `self.certifications` is only constructed when there IS a
  // current_model id (`if (self.current_model?.id) self.certifications = new
  // CertificationCollection({id})`, js:458) and the multiCheckbox wiring is
  // likewise gated on `self.current_model?.get("id")` (js:886) — the synthesized
  // header model has no id, so legacy NEVER fetches
  // `/ahu/plate_heat_exchangers/<id>/certifications/` and the certification
  // read-out simply does not render (see the certsQ render site).
  const certsQ = useGetTypeCertificationsQuery(
    { plural: 'plate_heat_exchangers', id: modelId },
    { skip: !modelId }
  );

  // ---- state model (legacy state Backbone model) ----
  const state = useRef<Record<string, unknown>>({});
  const init = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  /**
   * ★ Bumped by BOTH seed effects (header + board) on the commit that latches
   * `seeded.current = modelId`. Every post-seed effect that must fire exactly
   * once, on that commit, but whose dep list does NOT already carry
   * `seeded.current`, keys on this.
   *
   * Why: with the freshness gate above, the seed no longer lands on the commit
   * `detail` first arrives. On a REOPEN both `detail` and `phxDoc` are served
   * from cache on the very first render, so the two `[…, phxDoc]` effects below
   * run in that commit, bail on `seeded.current` (still null, refetch in
   * flight) — and then never run again, because the refetch comes back
   * deep-equal and RTK Query's structuralSharing hands back the SAME object
   * reference. Measured: the mount cascade never fired and the dialog's option
   * lists came back 151 options instead of 171. Same trap the coil and the
   * energy wheel hit.
   */
  const [seedTick, setSeedTick] = useState(0);
  const markSeeded = useCallback(() => setSeedTick((t) => t + 1), []);

  // userArmed gate (arms ONLY on a real #rate/#select click).
  const userArmed = useRef(false);
  const { dispatchRating, abortRating, isRating } = useRatingDispatch(
    headerMode ? 'header' : 'board'
  );
  // the rated_phx_model write (PATCH ahu/plate_heat_exchangers/rating_mode/<id>).
  // Fired ONLY inside doSelect (userArmed) — never on mount/open/browse.
  const [patchRatedPhx] = usePatchRatedPhxMutation();

  const [activeTab, setActiveTab] = useState<InputTab>('Properties');
  const [season, setSeason] = useState<SeasonTab>('winter'); // legacy default tab = winter.
  // sub-tab state (tmpl Properties 4 sub-tabs + Bypass 3 sub-tabs).
  const [propSubTab, setPropSubTab] = useState<PropSubTab>('general');
  const [bypassSubTab, setBypassSubTab] = useState<BypassSubTab>('general');

  // ---- ★ JOB A restored loaders (dedicated URLs, NOT the dependency-graph
  // param URL): size (getSize), module_configuration (getModuleConfigurations),
  // mfg settings (getMfgSettings → max_allowed_apd for the max_allowed_dp cap).
  // calc_mode_id, plate_pattern_id, and the bypass/recirc dropdowns already load
  // through the dependency graph / phx CMS doc — only these three need wiring. ----
  const [sizes, setSizes] = useState<OptionRow[]>([]);
  const [moduleConfigs, setModuleConfigs] = useState<OptionRow[]>([]);
  const [maxAllowedApd, setMaxAllowedApd] = useState<number | null>(null);
  // Render-time mirror (same pattern as ratedRowsRef below). doReset is captured
  // ONCE by the fan-out subscription effect (see the deps note there), so it must
  // read the mfg-settings cap through a ref or it would see the mount-time null.
  const maxAllowedApdRef = useRef<number | null>(null);
  maxAllowedApdRef.current = maxAllowedApd;
  // `buildRateInput` resolves `size` off this list (legacy dataForRating is fed
  // by the option-list load, js:1143), so it needs a render-time mirror too.
  const sizesRef = useRef<OptionRow[]>([]);
  sizesRef.current = sizes;

  // ★ `activate.project` is NEVER populated — the board's only
  // `rating_mode:activate` emitter (useComponentForm.openRatingStub) sends
  // {name, loading, current_model, section, section2}. CoilSelector and
  // GasHeaterSelector each hit this and each fixed it locally the same way; PHX
  // still read it, so the Altitude row printed 0 whatever the project said and
  // the rate went out at sea level (legacy: `project.elevation`, tmpl:903 for
  // the row, `dataForRating(+value,'elevation')` js:1623-1640 for the payload —
  // Heatex feeds it into the air-density calculation, `part_of_json_params`).
  // The board bootstrap already fetched this project, so the cache is warm.
  const { data: boardProject } = useGetAhuBoardProjectQuery(
    (ahu as { project_id?: number } | null | undefined)?.project_id ?? skipToken
  );
  /** render-time mirrors for `buildRateInput` (a `useCallback` that must not
   *  re-create per query result). */
  const ahuRef = useRef<typeof ahu>(null);
  ahuRef.current = ahu;
  const detailRef = useRef<Record<string, unknown> | undefined>(undefined);
  detailRef.current = detail;
  const elevationRef = useRef<unknown>(null);
  elevationRef.current =
    (boardProject as Record<string, unknown> | undefined)?.elevation ??
    (activate.project as Record<string, unknown> | null | undefined)?.elevation ??
    null;

  // ★ legacy `phxData` — the CATALOGUE row of the PICKED CANDIDATE, refetched on
  // every render_outputs (js:2965-2996): a `PhxMasterCollection` over
  // `plate_heat_exchangers/all/` whose ResultsFilter is
  // `{...state.attributes, ...rated_phxs[idx]}` narrowed to six fields. Legacy
  // reads `casing_depth` / `plate_spacing` / `plate_thickness` / `shape_id` off
  // `toJSON()[0]` — for the output sheet AND for the Select payload
  // (js:2120-2143). The port passed the component's saved `master_phx` instead,
  // reasoning that it is the same row; it is not — on an unrated/never-Selected
  // phx `master_phx` is empty, which is why dev3 printed no Plate Spacing, no
  // Plate Thickness and a bare `14.17 x 31.69 x` where dev prints the depth.
  const [phxCatalog, setPhxCatalog] = useState<Record<string, unknown> | null>(null);
  const phxCatalogRef = useRef<Record<string, unknown> | null>(null);
  phxCatalogRef.current = phxCatalog;

  // certification multiCheckbox (downgrade fix): the SELECTED set (ids) tracked
  // locally + the original set for the commit-on-select diff (§49.5). The
  // AVAILABLE list is masterLists.certification_id (dependency-loaded). Board mode
  // only — legacy builds the multiCheckbox solely when current_model.id exists
  // (plate_heat_exchanger.selector.js:939).
  const [selectedCerts, setSelectedCerts] = useState<Set<number>>(new Set());
  const certOriginal = useRef<Set<number>>(new Set());
  const [certListOpen, setCertListOpen] = useState(false);
  const [createTypeCertification] = useCreateTypeCertificationMutation();
  const [deleteTypeCertification] = useDeleteTypeCertificationMutation();

  // the rated models from a fresh Rate (read-only: the saved model renders as the
  // single stored row).
  const [ratedRows, setRatedRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [ratedIndex, setRatedIndex] = useState(0);
  /** which OUTPUT tab is open (legacy `activeOutputTabId`, js:3037-3050). It is
   *  remembered across a `changePhx` re-render and forced onto Notes by an
   *  error; the default follows the INPUT pane's tab (js:3052-3060). */
  const [outputTab, setOutputTab] = useState<PhxOutputTab | null>(null);
  const outputTabRef = useRef<PhxOutputTab | null>(null);
  outputTabRef.current = outputTab;
  /** legacy `self.version` — the vendor DLL release off the rate response. */
  const [rateVersion, setRateVersion] = useState<string | null>(null);
  /** legacy `self.from_api` — set only by a rate that produced a model list or
   *  an empty-but-error-free one; it is what makes `notFound` reachable. */
  const fromApi = useRef(false);
  // Render-time mirrors so the header-mode `saveInStorage` (legacy reads the
  // synchronous `self.rated_phxs` / `self.rated_phx_index`) has a default source;
  // call sites that mutate them in the SAME tick pass explicit overrides.
  const ratedRowsRef = useRef<Array<Record<string, unknown>> | null>(null);
  ratedRowsRef.current = ratedRows;
  const ratedIndexRef = useRef(0);
  ratedIndexRef.current = ratedIndex;
  const [rateErrors, setRateErrors] = useState<string[]>([]);
  const [rateWarnings, setRateWarnings] = useState<string[]>([]);
  const ratedDatetime = useRef<string | null>(null);

  // master param lists from the dependency graph (field → option-id list).
  const [masterLists, setMasterLists] = useState<Record<string, OptionRow[]>>({});
  const masterListsRef = useRef<Record<string, OptionRow[]>>({});
  masterListsRef.current = masterLists;

  // ---- ★ Phase 6.5 — HEADER-MODE STANDALONE SEED ---------------------------
  // plate_heat_exchanger.selector.js:428-449:
  //   if (self.header) self.dataForRate = {...self.rated_default_values};   (:430)
  //   if (!self.header || !saved_data) { current_model = new
  //       Backbone.Model(default_values); if (header) storage_saved_data = {}; }
  //   else {
  //     self.state.set(model.state);                                        (:437)
  //     self.model         = new Backbone.Model({...rated_default_values,
  //                            ...model.state, ...model.rated_phx, generic_mfg}); (:441)
  //     self.current_model = new Backbone.Model({...default_values,
  //                            ...model.current_model, ...model.state, generic_mfg}); (:442)
  //     self.dataForRate = model.dataForRate; self.dataForSave = model.dataForSave;
  //     self.needToRate  = model.rate;  self.rated_phx_index = model.rated_phx_index;
  //     self.storage_saved_data = saved_data;
  //   }
  //
  // ★★ RESOLUTION of builder 1's `current_model.toJSON()` question for PHX: the
  // writer here does NOT call `.toJSON()` at all — it is
  // `JSON.parse(JSON.stringify(self.current_model))` (js:1473), i.e. the raw
  // Backbone MODEL. `JSON.stringify` honours the object's own `toJSON` method, and
  // `Backbone.Model.prototype.toJSON` returns the attributes clone, so the result
  // is identical to `.toJSON()` — no throw, and the `current_model` member IS
  // written. (Same conclusion as ewheel: header mode SYNTHESIZES current_model.)
  //
  // ★ `model.rated_phx` (:441) is read but NO writer ever emits it — spreading
  // `undefined` is a no-op. Legacy dead read, mirrored by simply not emitting it.
  /** legacy `self.storage_saved_data`. */
  const storageMapRef = useRef<IndependentRatingMap>({});
  /** legacy `self.mfg_id` (the mfg option's `data-id`) — KEYS the entry (js:1468
   * `self.mfg_id || self.state.get('mfg_id')`). */
  const phxMfgIdRef = useRef<unknown>(null);
  /** legacy `self.rated_phx_index` / the saved index (js:447). */
  const savedRatedIndex = useRef<number | null>(null);
  /** legacy `self.dataForSave = {modelField:{}, searchField:{}}` (js:210-213). */
  const dataForSaveRef = useRef<{ modelField: Record<string, unknown>; searchField: Record<string, unknown> }>(
    { modelField: {}, searchField: {} }
  );
  /** legacy `self.dataForRate` — seeded from `rated_default_values` in header
   * mode (js:430). */
  const dataForRateRef = useRef<Record<string, unknown>>({});
  /** legacy `self.needToRate` (js:446) + `self.needToSearch`, folded into one
   * pending slot; resolved from the cascade `finished` hook (js:791-797). */
  const pendingRestore = useRef<{
    entry: IndependentRatingEntry;
    applyState: boolean;
  } | null>(null);

  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current === modelId) return;
    const map = readRatingMap(RATING_STORAGE_KEYS.plate_heat_exchanger);
    const entry = findLastEntry(map);
    const s: Record<string, unknown> = { ...HEADER_PHX_DEFAULTS };
    if (entry) {
      // js:437/:442 — `state` is what lands in the live state model AND is
      // re-spread over current_model; React has ONE flat state.
      Object.assign(
        s,
        (entry.current_model as Record<string, unknown>) ?? {},
        (entry.state as Record<string, unknown>) ?? {}
      );
      if (entry.generic_mfg != null) s.generic_mfg = entry.generic_mfg;
      phxMfgIdRef.current = s.mfg_id ?? null;
      savedRatedIndex.current = (entry.rated_phx_index as number | null) ?? null;
      dataForRateRef.current = (entry.dataForRate as Record<string, unknown>) ?? {};
      dataForSaveRef.current =
        (entry.dataForSave as { modelField: Record<string, unknown>; searchField: Record<string, unknown> }) ??
        { modelField: {}, searchField: {} };
      // js:446 `self.needToRate = model.rate` → the cascade `finished` hook
      // (js:791-793) re-fires the rate. Gated on `rate`, like ewheel.
      if (entry.rate) pendingRestore.current = { entry, applyState: false };
    } else {
      phxMfgIdRef.current = s.mfg_id ?? null;
    }
    storageMapRef.current = map;
    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;
    markSeeded();
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMode, modelId]);

  /**
   * `saveInStorage` (plate_heat_exchanger.selector.js:1465-1479). Guarded exactly
   * like legacy: `if (!self.storage_saved_data || !self.header) return;`.
   *
   * ★ MEMBER ORDER is byte-parity with js:1469-1478 —
   *   state, model_data, rate, last, current_model, rated_phx_index, generic_mfg,
   *   dataForRate, dataForSave
   * (JSON.stringify preserves insertion order.)
   *
   * ★★ PHX IS THE ONE WRITER THAT DOES **NOT** CLEAR `last` ON THE OTHER ENTRIES.
   * fan (js:2382-2384), coil (js:1480) and ewheel (js:1388) all open with
   * `Object.values(self.storage_saved_data).forEach((el) => el.last = false)`;
   * js:1465-1479 has no such line. Two rated manufacturers therefore leave TWO
   * `last:true` entries and the seed reader's `.find((el) => el.last)` picks the
   * FIRST in insertion order, not the newest. Reproduced via
   * `writeRatingEntry(..., clearLast:false)` — PARITY-ONLY, do NOT "fix".
   *
   * ★ `generic_mfg: !!(self.state.get('mfg_id') && self.mfg_id !=
   * self.state.get('mfg_id'))` (js:1476) — a BOOLEAN here (unlike ewheel's
   * `self.generic_mfg || self.state.get('mfg_id')`), true only when the option's
   * `data-id` differs from the select value.
   *
   * ★ `model_data` is legacy's CONSTRUCTOR OPTIONS object (`self.model_data =
   * options` at js:1451, then `model_data.current_model = self.current_model` /
   * `model_data.rated_phx_model = self.model` at :1453-1455). In header mode the
   * chassis passes `{name, ahu, project, section, current_model, header,
   * rated_phx_model}` (component_selector.js:150-159) with everything but `name`
   * and `header:true` undefined, and JSON.stringify DROPS undefined members — so
   * the serialised shape is exactly `{name, current_model, header,
   * rated_phx_model}` in that key order. Emitted verbatim for byte-shape parity —
   * note the PHX READER never touches `model_data` at all (js:437-448 reads only
   * state / rated_phx / generic_mfg / current_model / dataForRate / dataForSave /
   * rate / rated_phx_index), so it is write-only ballast on both sides.
   */
  const saveInStorage = useCallback(
    (over?: { rate?: boolean; ratedIndex?: number | null }) => {
      if (!headerMode) return;
      const flat = JSON.parse(JSON.stringify(state.current)) as Record<string, unknown>;
      storageMapRef.current = writeRatingEntry(
        RATING_STORAGE_KEYS.plate_heat_exchanger,
        phxMfgIdRef.current ?? state.current.mfg_id,
        {
          state: flat,
          model_data: {
            name: eventName,
            current_model: flat,
            header: true,
            rated_phx_model: (ratedRowsRef.current?.[ratedIndexRef.current] ?? {}) as Record<
              string,
              unknown
            >,
          },
          // legacy `rate: !!self.rated_phxs` — the ARRAY, not a row.
          rate: over?.rate ?? Boolean(ratedRowsRef.current?.length),
          last: true,
          current_model: flat,
          rated_phx_index:
            over && 'ratedIndex' in over ? (over.ratedIndex ?? 0) : ratedIndexRef.current,
          generic_mfg: Boolean(
            state.current.mfg_id && phxMfgIdRef.current != state.current.mfg_id
          ),
          dataForRate: JSON.parse(JSON.stringify(dataForRateRef.current)),
          dataForSave: JSON.parse(JSON.stringify(dataForSaveRef.current)),
        } as unknown as IndependentRatingEntry,
        storageMapRef.current,
        // ★ PHX does NOT clear `last` — see the note above (js:1465-1479).
        false
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerMode, eventName]
  );

  // ---- seed state once GET resolves (BOARD host) ----
  useEffect(() => {
    if (headerMode) return;
    if (!detail) return;
    // ★ the reopen gate (see `detailFresh` above): never seed from a row that
    // predates this open — on a reopen after Select that row is the pre-Select
    // one and the Output pane renders empty.
    if (!detailFresh) return;
    if (seeded.current === modelId) return;
    const m = detail as PhxModel;
    const p = parseParams(m.plate_heat_exchanger_params);
    const s: Record<string, unknown> = {
      ...PHX_DEFAULTS,
      // params blob ids (the search-field selection).
      mfg_id: p.mfg_id ?? (m.master_phx as any)?.mfg_id,
      series_id: p.series_id ?? (m.master_phx as any)?.series_id,
      size_id: p.size_id,
      shape_id: p.shape_id,
      // flow_type_id lives in the params JSON (not a model column) — seed it from
      // there (was blank: the MODEL_FIELDS loop only reads m.flow_type_id which is
      // undefined on the component row, so the saved Crossflow selection was lost).
      flow_type_id: p.flow_type_id ?? (m as Record<string, unknown>).flow_type_id,
      module_configuration_id: p.module_configuration_id ?? PHX_DEFAULTS.module_configuration_id,
      plate_spacing_code_id: p.plate_spacing_code_id,
      plate_spacing: p.plate_spacing,
      casing_height: m.casing_height ?? (m.master_phx as any)?.casing_height,
      casing_depth: m.casing_depth ?? (m.master_phx as any)?.casing_depth,
    };
    // copy the model-field selects/inputs straight off the saved row.
    const MODEL_FIELDS = [
      'temp_rating_id', 'seal_id', 'configuration_id', 'mounting_id',
      'plate_orientation_id', 'plate_mtl_id', 'plate_finish_id',
      'end_plate_style_id', 'end_plate_mtl_id', 'end_plate_finish_id',
      'corner_profile_style_id', 'corner_profile_mtl_id', 'corner_profile_finish_id',
      'bypass_dmr_mtl_id', 'bypass_dmr_fin_id', 'bypass_location_id', 'bypass_type_id',
      'bypass_side_id', 'bypass_dmr_config_id', 'bypass_dmr_drive_location_id',
      'bypass_dmr_location_id', 'dmrs_supplied_by_id', 'recirc_dmr_config_id',
      'recirc_dmr_location_id', 'flow_type_id', 'type_desired_efficiency_id',
      'certification_id', 'face_one_definition_id', 'face_two_definition_id',
      'plate_pattern_id', 'calc_mode_id', 'pricing_method_id',
      'min_desired_efficiency', 'bypass_width', 'bank_wide', 'bank_high', 'bank_deep',
      'casing_width', 'height_from_floor', 'dist_from_left', 'module_qty',
      'cost_product_user', 'cost_freight_user',
      'price_product_user', 'price_freight_user', 'max_allowed_dp',
    ];
    for (const f of MODEL_FIELDS) {
      if (m[f] != null) s[f] = m[f];
      else if (PHX_DEFAULTS[f] != null) s[f] = PHX_DEFAULTS[f];
    }
    // per-season oa/sa/ra/ea perf fields straight off the saved row.
    for (const sea of ['summer', 'winter'] as const) {
      for (const pre of ['oa', 'sa', 'ra', 'ea'] as const) {
        for (const f of [
          'air_stream', 'airflow_actual', 'db_temp', 'wb_temp', 'rh',
          'face_velocity', 'apd', 'condensation_rate', 'frost_surface_percent',
          'inlet_pressure',
        ]) {
          const key = f === 'air_stream' ? `${pre}_air_stream_${sea}_id` : `${pre}_${f}_${sea}`;
          if (m[key] != null) s[key] = m[key];
          else if (PHX_DEFAULTS[key] != null) s[key] = PHX_DEFAULTS[key];
        }
      }
      // season totals/effectiveness off the saved row.
      for (const f of [
        'wet_temp_effectiveness', 'dry_temp_effectiveness', 'wet_temp_ratio',
        'dry_temp_ratio', 'frost_starting_temp', 'hrs_capacity', 'erp_1253_2014',
        'efficiency_' + sea + '_ashrae',
      ]) {
        const key = f.endsWith('_ashrae') ? f : `${f}_${sea}`;
        if (m[key] != null) s[key] = m[key];
      }
    }
    s.oa_sp = m.oa_sp ?? PHX_DEFAULTS.oa_sp;
    // Dev-sync J5 — the chart's pending re-rate handoff, COMPONENT-scoped
    // (plate_heat_exchanger.selector.js:432-436 gate + :841-873 apply +
    // :1578 consume): the override merges onto the render data — here the
    // seed state IS the render data, so the fields land directly; the
    // catalog pick clears (master_phx_id null, :858) and the dialog lands
    // on Performance with the stale output blanked (:871/:1603 — expressed
    // by not seeding the stored rated row below).
    const pendingFields = takeComponentPendingRerate(modelId, [
      (m as Record<string, unknown>).section_id as number | undefined,
      (m as Record<string, unknown>).section_2_id as number | undefined,
    ]);
    if (pendingFields) {
      Object.assign(s, pendingFields);
      s.master_phx_id = null;
      setActiveTab('Performance');
    }
    state.current = s;
    init.current = { ...s };
    seeded.current = modelId;
    markSeeded();
    // render the STORED rated row read-only (legacy render_outputs initial).
    //
    // ★ Gated on `model`, NOT on `master_phx_id`. Legacy's initial output pass is
    //   render: `if (self.rated_phx.model && !self.changed) self.render_outputs(…)`
    //          (plate_heat_exchanger.selector.js:1642-1644)
    //   setOutputs: `if (!self.rated_phxs) self.rated_phxs =
    //                [self.current_model.toJSON()]`                        (:1895)
    // — i.e. the gate is the saved row's `model` NAME, and the row it draws is
    // the detail row itself. `master_phx_id` cannot stand in for it: a PHX in
    // rating mode (`select_mode.value == 'rating'`, which is the default and
    // what this dialog writes) has its `master_phx` FORCED to None on every
    // save — `models_component_plate_exchanger.py:618-624` nulls it and moves
    // the pick into the AHUPHXRatingMode row instead. So `master_phx_id` is
    // null for every rated PHX (measured on dev3: 19 rows, 19 in rating mode,
    // 0 with a master_phx_id) and this branch never ran — the reporter's
    // reopened dialog drew no Output pane at all.
    //
    // `model` is the serializer field for `AHUPHX.model`, which in rating mode
    // returns `self.phx_rating_mode.model` (:717) — the vendor model name the
    // Select PATCH writes, and null on a never-rated row. Same truthiness test
    // legacy applies.
    if (m.model && !pendingFields) {
      setRatedRows([m as Record<string, unknown>]);
      ratedDatetime.current = (m.rating_datetime as string) ?? null;
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, detailFresh, modelId, bump, headerMode]);

  // ---- DEPENDENCY ENGINE (the cascade) ----
  // loadParams resolves a field's option list from the phx CMS doc (doc-resolved
  // fields) or the master param branch. Read-only seeds the full list; the engine
  // narrows by parents on a parent change.
  const loadParams = useCallback<LoadParamsFn>(
    async (field, parents) => {
      // doc-resolved fields → the CMS doc full list (read-only superset).
      if (isPhxCmsField(field)) {
        return phxCmsOptionList(phxDoc as PhxCmsDoc, field).map((r) => ({
          ...r,
          id: r.id as number,
        }));
      }
      // master param branch → fetch the narrowed list by the parent filters.
      const url = phxParamUrl(field, parents, state.current);
      if (!url) return masterListsRef.current[field] ?? [];
      try {
        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) return masterListsRef.current[field] ?? [];
        const body = await res.json();
        const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
        setMasterLists((prev) => ({ ...prev, [field]: rows }));
        return rows.map((r: any) => ({ ...r, id: r.id ?? r.value ?? r }));
      } catch {
        return masterListsRef.current[field] ?? [];
      }
    },
    [phxDoc]
  );

  const getValue = useCallback((field: string) => state.current[field] as any, []);
  const setStateValue = useCallback(
    (field: string, value: any) => {
      state.current = { ...state.current, [field]: value };
      bump();
    },
    [bump]
  );
  const shouldSetDefaultIfNull = useCallback((field: string) => {
    const req = PHX_REQUIRED_FIELDS[Number(state.current.mfg_id)];
    return req ? req.has(field) : true;
  }, []);

  /**
   * ★ W6.5 F3 — the OPEN-cascade preserve gate, which this selector never wired
   * (FanSelector.tsx:1843 is the reference). `applyNewValues` re-picks a field
   * whenever the freshly-loaded list omits its current value, and on the mount
   * cascade a list can legitimately come back EMPTY — `phxParamUrl` returns null
   * when a declared parent is unset (`size_id` is a parent of `end_plate_style_id`
   * through the `size_id: {dependencies:['end_plate_style_id']}` edge), and
   * `loadParams` then falls back to the not-yet-populated cache. With no
   * `isArmed`, the re-pick ran on OPEN and wrote `null` over the saved value:
   * measured on the reporter's unit, `end_plate_style_id` is 1 ("AE") on the
   * component row and `null` in the dialog, so the rate went out with
   * `end_plate_style: null` where dev sends `"ae"` — a different `OpzAE`, hence
   * a different candidate list. Armed only by a real user parameter change, as
   * legacy is (the narrowing/drop belongs to `changeSearchField`).
   */
  const cascadeArmed = useRef(false);
  const isArmed = useCallback(() => cascadeArmed.current, []);
  const graph = useDependencyGraph({
    spec: PHX_DEPENDENCY_GRAPH,
    loadParams,
    getValue,
    setValue: setStateValue,
    shouldSetDefaultIfNull,
    isArmed,
  });

  // mount cascade: once seeded, load every master-list option set filtered by the
  // saved mfg/series so the dependent selects render their LABELS (not the raw
  // id). applyNewValues keeps the saved value (it's always in its own list), so
  // this never mutates the model — purely populates the option lists for display.
  const cascadeFired = useRef(false);
  useEffect(() => {
    if (cascadeFired.current) return;
    if (seeded.current !== modelId) return;
    if (state.current.mfg_id == null) return;
    // ★ HEADER MODE: `detail` never lands (the fetch is skipped), so wait on the
    // CMS doc instead — legacy runs the same full parameter load on construction
    // (31 GETs observed live on the first PLATE HEAT EXCHANGER tab visit,
    // discovery §2) so the master-list selects render LABELS not raw ids.
    if (headerMode && !phxDoc) return;
    cascadeFired.current = true;
    graph.onParentChange('mfg_id');
    if (state.current.series_id != null) graph.onParentChange('series_id');
    // ★ `seedTick` — NOT `detail`/`phxDoc` alone: the freshness gate moves the
    // seed off the commit those first arrive, and on a reopen both are served
    // from cache and come back reference-identical, so this dep list would
    // never change again and the cascade would never fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, detail, headerMode, phxDoc, seedTick]);

  // ---- ★ JOB A dedicated option loaders (read-only; never mutate the model) ----
  // module configurations (legacy getModuleConfigurations, js:2425-2433) — a flat
  // list, fetched once. Feeds the module_configuration_id ★ rate field.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(PHX_MODULE_CONFIGS_URL, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok || !alive) return;
        const body = await res.json();
        const rows: OptionRow[] = Array.isArray(body) ? body : (body?.results ?? []);
        if (alive) setModuleConfigs(rows);
      } catch {
        /* read-only load failure → empty list (legacy returns []). */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seeded.current]);

  // sizes (legacy getSize, js:2434-2445) — filtered by mfg + series. Re-fetches on
  // a series change. size_id is a CATALOGUE filter (search field), NOT a rate field.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const mfgId = state.current.mfg_id;
    const seriesId = state.current.series_id;
    if (mfgId == null || seriesId == null) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          phxSizesUrl(mfgId as number, seriesId as number, state.current.casing_height as number),
          { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } }
        );
        if (!res.ok || !alive) return;
        const body = await res.json();
        const rows: OptionRow[] = Array.isArray(body) ? body : (body?.results ?? []);
        if (alive) setSizes(rows);
      } catch {
        /* read-only. */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seeded.current, state.current.mfg_id, state.current.series_id]);

  // mfg settings (legacy getMfgSettings, js:2416-2423) — supplies max_allowed_apd,
  // the data-max cap for the max_allowed_dp Settings input (tmpl:1123).
  useEffect(() => {
    if (seeded.current !== modelId) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(PHX_MFG_SETTINGS_URL, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok || !alive) return;
        const body = await res.json();
        const cap = (body?.max_allowed_apd ?? (Array.isArray(body) ? body[0]?.max_allowed_apd : null)) as
          | number
          | null;
        if (alive && cap != null) setMaxAllowedApd(Number(cap));
        // ★ NOT SEEDED onto max_allowed_dp: a new/unsaved PHX now defaults the
        // field to 1.5 (PHX_DEFAULTS.max_allowed_dp), which — unlike the old
        // empty default — is always a valid non-null PD, so the vendor call
        // never goes out with no cap. The mfg cap still supplies `data-max`
        // via `setMaxAllowedApd` above, and still seeds the field on #reset
        // (doReset, via maxAllowedApdRef) once it has loaded.
      } catch {
        /* read-only. */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seeded.current]);

  // ★ the picked candidate's CATALOGUE row (legacy render_outputs js:2965-2996,
  // see the note on `phxCatalog`). Refetched per rate and per pager click, which
  // is exactly when legacy rebuilds the collection.
  useEffect(() => {
    const row = ratedRows?.[ratedIndex];
    if (!row) {
      setPhxCatalog(null);
      return undefined;
    }
    // `Object.assign({}, state.attributes, ratedDataaa)` narrowed to the
    // ResultsFilter's allowed_fields; ResultsFilter.set drops
    // undefined/null/''/'None'/'none' (results_filter.js:44-56).
    const merged: Record<string, unknown> = { ...state.current, ...row };
    const query = ['series_id', 'mfg_id', 'flow_type_id', 'size_id', 'plate_spacing_code_id', 'casing_height_mm']
      .filter((k) => {
        const v = merged[k];
        return !(v === undefined || v === null || v === '' || v === 'None' || v === 'none');
      })
      .map((k) => `${k}=${merged[k]}`)
      .join('&');
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${PHX_CATALOG_BASE_URL}?${query}`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok || !alive) return;
        const body = await res.json();
        const rows = (Array.isArray(body) ? body : (body?.results ?? [])) as Array<
          Record<string, unknown>
        >;
        if (alive) setPhxCatalog(rows[0] ?? null);
      } catch {
        /* read-only — the template falls back to the rated row. */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratedRows, ratedIndex]);

  // certification multiCheckbox seed (downgrade fix): the saved /certifications/
  // relation → the SELECTED set + the commit-diff baseline. Board mode only
  // (certsQ is skipped when !modelId, matching legacy js:939).
  useEffect(() => {
    if (!certsQ.data) return;
    const ids = new Set<number>(
      (certsQ.data as Array<{ id?: number }>).map((c) => Number(c.id)).filter((n) => !Number.isNaN(n))
    );
    setSelectedCerts(ids);
    certOriginal.current = new Set(ids);
  }, [certsQ.data]);

  // total_width_max / total_height_max seed from the section's available airway
  // (legacy `value="{{aw_width}}"`/`{{aw_height}}`, tmpl:175/192, populated from
  // current_model.section aw_width/aw_height — js:3422-3425). Read-only seed.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const sec = activate.section as Record<string, unknown> | undefined;
    if (!sec) return;
    let touched = false;
    if (state.current.total_width_max == null && sec.aw_width != null) {
      state.current = { ...state.current, total_width_max: sec.aw_width };
      touched = true;
    }
    if (state.current.total_height_max == null && sec.aw_height != null) {
      state.current = { ...state.current, total_height_max: sec.aw_height };
      touched = true;
    }
    if (touched) bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seeded.current, activate]);

  // ---- field change handlers ----
  const markChanged = useCallback(() => {
    bus.emit('rating_mode:mounting:changed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus]);

  const onSearchField = useCallback(
    (name: string, raw: string) => {
      const value = name.endsWith('_id') ? num(raw) : raw;
      // a real user parameter change ARMS the cascade: from here on the engine
      // narrows and drops, exactly as legacy's changeSearchField does.
      cascadeArmed.current = true;
      state.current = { ...state.current, [name]: value };
      // ★ Switching manufacturer re-seeds that vendor's model defaults —
      // configuration, calculation mode, mounting, seal, plate/end-plate/corner
      // finishes. Legacy does this in changeSearchField's mfg_id branch
      // (`updateDefaultsDependsOnMFG(value)`, js:2318-2321), i.e. on EVERY mfg
      // change, not only on Reset. The port applied PHX_MFG_DEFAULTS from doReset
      // alone, so picking Heatex left the previous vendor's Calculation Mode,
      // Mounting and Seal in place and Heatex's calc mode 4 never appeared.
      if (name === 'mfg_id') {
        const mfgDefaults = PHX_MFG_DEFAULTS[Number(value)];
        if (mfgDefaults) {
          state.current = { ...state.current, ...mfgDefaults };
          if (headerMode) Object.assign(dataForSaveRef.current.modelField, mfgDefaults);
        }
      }
      // a search-field (param) change invalidates the prior rated outputs.
      setRatedRows(null);
      if (PHX_DEPENDENCY_GRAPH[name]) graph.onParentChange(name);
      // ★ HEADER MODE — the same changeSearchField shape as ewheel:
      //   dataForSave.searchField[name] = value, then mfg_id → findFromStoraga
      //   (RESTORE that manufacturer's entry) else saveInStorage().
      if (headerMode) {
        dataForSaveRef.current.searchField[name] = value;
        if (name === 'mfg_id') {
          phxMfgIdRef.current = value ?? null;
          const entry = findEntryByMfg(storageMapRef.current, value);
          if (entry) pendingRestore.current = { entry, applyState: true };
        } else {
          saveInStorage();
        }
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, markChanged, bump, headerMode, saveInStorage]
  );

  const onModelField = useCallback(
    (name: string, raw: string | null) => {
      const value = name.endsWith('_id') ? (raw == null ? null : num(raw)) : raw;
      // Dev-sync J4 — disableSyncIfChanged (plate_heat_exchanger.selector.js:
      // 2245-2284, byte-identical to the ewheel pair): a genuine edit of one
      // of the 12 per-season OA/RA air-condition fields while sync is on
      // silently disables synchronize for the whole unit. Board mode only.
      if (!headerMode) disableSyncIfAirFieldEdited(name, state.current[name], value);
      state.current = { ...state.current, [name]: value };
      // calc_mode_id drives bank wide×high geometry (legacy changeModelField →
      // setBankWideByHigh, js:2294-2298 / :1670-1703). model_field, so it lands
      // here (not onSearchField). Mutates bank_wide/high/deep in place.
      if (name === 'calc_mode_id') applyCalcMode(state.current, num(raw ?? ''));
      setRatedRows(null);
      // changeModelField: `dataForSave.modelField[name] = value` then
      // `saveInStorage()` (the ewheel/phx pair share this shape). Legacy records
      // the staged edit UNCONDITIONALLY (js:2330) — only `saveInStorage()` is
      // header-only machinery here. selectPhx reads the map back for the fields
      // it takes as staged-or-RATED (bypass_width, js:1944), so gating the
      // record on headerMode made a board-mode edit invisible at Select.
      dataForSaveRef.current.modelField[name] = value;
      if (headerMode) saveInStorage();
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markChanged, bump, headerMode, saveInStorage]
  );

  // certification multiCheckbox toggle (downgrade fix). Tracks the SELECTED set
  // locally; the actual /certifications/ relation is committed as a create/delete
  // DIFF on Select (commitPhxChain), matching the RTKQ "commit-on-close diff"
  // contract (ahuComponentFormsApi §49.5) and the userArmed data-safety model —
  // NOTHING writes on toggle. Legacy's SelectorMultiCheckbox syncs per-toggle, but
  // the parity target is the resulting relation state, which the diff reproduces.
  const toggleCertification = useCallback(
    (certId: number, checked: boolean) => {
      setSelectedCerts((prev) => {
        const next = new Set(prev);
        if (checked) next.add(certId);
        else next.delete(certId);
        return next;
      });
      markChanged();
    },
    [markChanged]
  );

  // ---- #rate required-field gate (checkRequiredFields) ----
  const requiredFilled = useMemo(() => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    const req = PHX_REQUIRED_FIELDS[mfg];
    if (!req) return false;
    // gate the always-required + the active-season perf blocks.
    for (const sea of ['summer', 'winter'] as const) {
      const hasAir = s[`oa_airflow_actual_${sea}`] != null && s[`oa_airflow_actual_${sea}`] !== '';
      if (!hasAir) continue;
      const fields = sea === 'summer'
        ? SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg]
        : WINTER_REQUIRED_FIELDS_FOR_RATE[mfg];
      for (const f of fields ?? []) {
        const v = s[f];
        if (v == null || v === '' || v === '--') return false;
      }
    }
    // at least one season must carry airflow.
    return (
      (s.oa_airflow_actual_summer != null && s.oa_airflow_actual_summer !== '') ||
      (s.oa_airflow_actual_winter != null && s.oa_airflow_actual_winter !== '')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail]);

  // a saved (rated) phx starts with Select ENABLED; an unrated one stays disabled
  // until a Rate (legacy checkButtonsAvailability).
  useEffect(() => {
    const m = (detail ?? {}) as PhxModel;
    // `model`, not `master_phx_id` — same reason as the stored-row seed above:
    // a rating-mode PHX never carries a master_phx_id, so this arm was dead and
    // a reopened, already-selected dialog offered a disabled SELECT.
    setSelectEnabled(Boolean(m.model) || Boolean(ratedRows?.length));
  }, [detail, ratedRows, setSelectEnabled]);

  // #rate visual gate — SCOPED to this selector's own chassis. An un-scoped
  // document.getElementById('rate') would hit the BOARD dialog's #rate first
  // (duplicate ids) whenever the header popup and a board dialog coexist.
  // Discovery §7.3 trap 1.
  useEffect(() => {
    const btn = chassisEl()?.querySelector<HTMLElement>('#rate');
    if (!btn) return;
    // ★ Phase 6.5 — OWN-TAB PREDICATE. The header popup never unmounts a visited
    // tab (component_selector.js:126-186 is show/hide), so all four selectors
    // stay mounted and would fight over the ONE shared `#rate`: whichever
    // rendered last won, and returning to Fan after visiting HEAT WHEEL / PLATE
    // HEAT EXCHANGER left RATE greyed out with valid inputs. Legacy is immune
    // because every gate is scoped to its own tab —
    // `$('#rate.all-rate[data-type="<own>"]')` (fan :1998/:6657, coil
    // :1656/:2677, ewheel :2469, phx :2427) — which matches NOTHING while
    // another tab is active. The chassis stamps `data-type` from the active tab
    // (component_selector.js:173/:181; IndependentRatingPopup `data-type={tab}`)
    // and each selector's `activate.name` IS that tab key, so comparing the two
    // reproduces legacy's scoping exactly.
    // HEADER-ONLY: the board hosts one dialog / one selector / one `#rate`, so
    // its behaviour stays byte-identical (and a board `data-type` that ever
    // disagreed with `eventName` could otherwise freeze the button).
    if (headerMode && btn.dataset.type !== eventName) return;
    btn.classList.toggle('non-clickable', !requiredFilled);
  });

  // ---- section-resize listeners (per BOTH sections — dual tunnel) ----
  useEffect(() => {
    const sec = activate.section as { id?: number; tunnel_id?: number } | undefined;
    const sec2 = (activate as any).section2 as { id?: number; tunnel_id?: number } | undefined;
    const offs: Array<() => void> = [];
    const onResize = () => bump();
    for (const ss of [sec, sec2]) {
      if (!ss) continue;
      if (ss.tunnel_id != null)
        offs.push(bus.on(`changed:tunnel_${ss.tunnel_id}:sizes` as any, onResize));
      if (ss.id != null)
        // NB the legacy event key is misspelled 'secrion' — match it verbatim.
        offs.push(bus.on(`changed:secrion_${ss.id}:casing_insulation_id` as any, onResize));
    }
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, bump, bus]);

  // ---- build the phx RATE input (legacy collectData + dataForRate + stripping) ----
  const buildRateInput = useCallback((): Record<string, unknown> => {
    const s = state.current;
    const mfg = Number(s.mfg_id);
    const input: Record<string, unknown> = {
      mfg_id: s.mfg_id,
      min_desired_efficiency: s.min_desired_efficiency,
      bank_wide: s.bank_wide,
      bank_high: s.bank_high,
      bank_deep: s.bank_deep,
      bypass_width: s.bypass_width,
      module_configuration_id: s.module_configuration_id,
      casing_width: s.casing_width,
      height_from_floor: s.height_from_floor ?? 0,
      // ★ JOB A restored: total_width_max/total_height_max reach the rate the same
      // way legacy pushes every valued search_field input through
      // getTreeOfAllInputs → dataForRating (plain-field branch, js:1260-1262).
      total_width_max: s.total_width_max,
      total_height_max: s.total_height_max,
      // ★ the three fields legacy pushes into `dataForRate` straight off the
      // component and AHU rows rather than off a control (js:1005-1007):
      //   dataForRating(current_model.dual_tunnel_air_rel_dirs, …)
      //   dataForRating(current_model.tunnel_type, …)
      //   ahu && dataForRating(ahu.tunnel_configuration_value, …)
      // `RecuperatorPHXHandler.before_fetching` keys the bypass model-code
      // lookup on them — `MasterAHUPHXModelCodeBypassDamper.objects.filter(
      // tunnel_type=…, dual_tunnel_air_rel_dirs=…, dual_tunnel_sa_pos=
      // f(tunnel_configuration_value, face_one, face_two))` — and only sets
      // `TipoByPass` when that row is found. Without them the filter matches
      // nothing, no bypass code is sent, and the vendor returns a different
      // candidate set (dev sends `TipoByPass: 12` for this unit; dev3 sent none).
      dual_tunnel_air_rel_dirs: (detailRef.current as PhxModel | undefined)?.dual_tunnel_air_rel_dirs,
      tunnel_type: (detailRef.current as PhxModel | undefined)?.tunnel_type,
      tunnel_configuration_value: (ahuRef.current as Record<string, unknown> | null | undefined)
        ?.tunnel_configuration_value,
    };
    for (const k of ['dual_tunnel_air_rel_dirs', 'tunnel_type', 'tunnel_configuration_value']) {
      if (input[k] == null || input[k] === '') delete input[k];
    }
    // calc-mode bank geometry normalization (legacy setBankWideByHigh's net effect
    // on dataForRate, js:1670-1701): mode 1 blanks wide/high; mode 3 forces 1×1.
    const calcMode = Number(s.calc_mode_id);
    if (calcMode === 1) {
      input.bank_wide = null;
      input.bank_high = null;
    } else if (calcMode === 3) {
      input.bank_wide = 1;
      input.bank_high = 1;
    }
    // season perf blocks (oa/ra). Legacy pushes these through
    // `dataForRating(+value, name)` (js:1637) — NUMBERS, where the port was
    // sending the raw input strings ("6000", "20"). Same values, different JSON
    // types on the wire to the vendor; match dev.
    const numeric = (v: unknown) =>
      v == null || v === '' || Number.isNaN(Number(v)) ? v : Number(v);
    for (const sea of ['summer', 'winter'] as const) {
      for (const pre of ['oa', 'ra'] as const) {
        input[`${pre}_air_stream_${sea}_id`] = s[`${pre}_air_stream_${sea}_id`];
        input[`${pre}_airflow_actual_${sea}`] = numeric(s[`${pre}_airflow_actual_${sea}`]);
        input[`${pre}_db_temp_${sea}`] = numeric(s[`${pre}_db_temp_${sea}`]);
        input[`${pre}_wb_temp_${sea}`] = numeric(s[`${pre}_wb_temp_${sea}`]);
        input[`${pre}_rh_${sea}`] = numeric(s[`${pre}_rh_${sea}`]);
      }
    }
    // resolve the doc-driven VENDOR CODES + master-list values (legacy
    // dataForRate uses itemValue.value, NOT itemValue.name — BUG 1). The visible
    // select display still uses phxCmsSelectedLabel (.name) at the render site;
    // here the rate input must carry the vendor code (.value) or fall to the id.
    for (const field of Object.keys(PHX_CMS_PATHS_LOCAL)) {
      const key = field.replace(/_id$/, '');
      input[key] =
        phxCmsSelectedValue(phxDoc as PhxCmsDoc, field, s[field] as any) ?? s[field];
    }
    for (const field of Object.keys(PHX_PARAMETER_NAMES)) {
      const key = field.replace(/_id$/, '');
      const rows = masterListsRef.current[field];
      const row = rows?.find((r: any) => String(r.id ?? r.value) == String(s[field]));
      // RESOLVE OR OMIT — never fall through to the raw id. Legacy's
      // `dataForRating` writes `itemValue.value`/`.name`, and when the lookup
      // finds nothing it either deletes the key or leaves it unset (js:1236-1248);
      // it has no id branch. The vendor mappings key on the CODE
      // (`end_plate_style_mapping['ae']`, `PLATE_MTL_FINISH_MAP[('aluminum',…)]`),
      // so an id reaches them as an unmapped value.
      const code = (row as any)?.value ?? (row as any)?.name;
      if (code != null && code !== '') input[key] = code;
      else delete input[key];
    }
    // ── ★ the fields legacy sends that this builder had no entry for ──────────
    // `dataForRate` is not a curated list on dev: `dataForRating(value, name)` is
    // called for EVERY named control — on render for each one that has a value
    // (js:1623-1640), on every change, and again whenever an option list lands
    // (js:1143). So a control that exists in the pane is in the payload. These
    // five are consumed by the vendor handlers and were absent:
    //   series  → Recuperator `Serie` / Heatex `series`. WITHOUT IT the vendor
    //     searches every series: measured on dev3, 120 s (read timeout, surfaced
    //     to the browser as a Cloudflare 504) against 15-18 s with it. That is
    //     the whole of "the output pane is blank after RATE" — the rate never
    //     returns, so the sheet falls back to the saved row.
    //   size / size_id → `RecuperatorPHXHandler.before_translating_to_target`
    //     looks the catalogue row up by size to fix `casing_height`; Heatex's
    //     `part_of_json_params` carries `size_id`.
    //   calc_mode → `ModoCalcolo` (Recuperator) / `CALC_MODE_MAP` (Heatex).
    //   max_allowed_dp → `DPmax`; Heatex defaults it to 0 when missing.
    //   elevation → Heatex air density (`part_of_json_params`). The Altitude row
    //     is read-only and reads the project, exactly as tmpl:903 does.
    // Each is stripped when unset, which is what legacy's `delete dataForRate[i]`
    // for a null/'--' value does — with the sole exception of size_id, see below.
    const seriesRow = (masterListsRef.current.series_id ?? []).find(
      (r: any) => String(r.id) === String(s.series_id)
    ) as Record<string, unknown> | undefined;
    // legacy's `itemValue.series` branch (js:1229) — the series rows are
    // `{id, series}`, with no name/value.
    const seriesCode = seriesRow?.value ?? (seriesRow as any)?.series ?? seriesRow?.name;
    if (seriesCode != null && seriesCode !== '') input.series = seriesCode;

    const sizeRow = sizesRef.current.find((r: any) => String(r.id) === String(s.size_id)) as
      | Record<string, unknown>
      | undefined;
    if (sizeRow) input.size = sizeRow.value ?? sizeRow.name;
    // size_id is the one field legacy ALWAYS sends, `null` included. It is absent
    // from both `removeItemsFormDataForRateIfNull` (js:1149-1169) and
    // `PARAMETER_POSSIBLE_VALUES_PATHS_MAPPING` (js:1170-1206), so it falls to the
    // plain-field branch `dataForRate[fieldName] = fieldValue` (js:1260-1262) and
    // carries whatever `state.get('size_id')` holds — `null` for the '--' option
    // (tmpl:98-100, `data-value="None"` → null in collectData, js:3312-3319).
    // Heatex's input processor then indexes it unconditionally —
    // `input_data["size_id"]` at
    // network_control/.../plate_heat_exchanger/heatex/components/input_processor.py:173
    // — so omitting the key raises KeyError and the pane shows `Error: 'size_id'`.
    // `null` is the correct "don't constrain the size search" value: the very next
    // line, `_get_sizes_styles`'s `if size_id:` guard, skips the filter and offers
    // the vendor every size in the series.
    input.size_id = s.size_id === '' || s.size_id === '--' ? null : (s.size_id ?? null);

    const calcRow = (masterListsRef.current.calc_mode_id ?? []).find(
      (r: any) => String(r.id) === String(s.calc_mode_id)
    ) as Record<string, unknown> | undefined;
    if (calcRow) input.calc_mode = calcRow.value ?? calcRow.name;

    if (s.max_allowed_dp != null && s.max_allowed_dp !== '') {
      input.max_allowed_dp = Number(s.max_allowed_dp);
    }
    // legacy keeps the id alongside the code whenever a spacing code is picked
    // (js:1250-1256) and deletes BOTH when it is not.
    if (input.plate_spacing_code == null || input.plate_spacing_code === '--') {
      delete input.plate_spacing_code;
      delete input.plate_spacing_code_id;
    } else {
      input.plate_spacing_code_id = s.plate_spacing_code_id;
    }
    // header mode drops it outright (getTreeOfAllInputs js:2541-2546).
    if (!headerMode) input.elevation = Number(elevationRef.current || 0);
    // Pressure In (Heatex only in the pane, but a plain model field either way).
    for (const sea of ['summer', 'winter'] as const) {
      for (const pre of ['oa', 'ra'] as const) {
        const v = s[`${pre}_inlet_pressure_${sea}`];
        if (v != null && v !== '') input[`${pre}_inlet_pressure_${sea}`] = v;
      }
    }
    // blank-season deletion (legacy 2655-2660): no oa_airflow ⇒ delete the
    // season's required fields.
    if (input.oa_airflow_actual_winter == null || input.oa_airflow_actual_winter === '--') {
      (WINTER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => delete input[k]);
    }
    if (input.oa_airflow_actual_summer == null || input.oa_airflow_actual_summer === '--') {
      (SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => delete input[k]);
    }
    // bypass_width dropped for Heatex (legacy 2661-2663).
    if (mfg == 52) delete input.bypass_width;
    // module_configuration_id stripped when falsy (legacy 2664-2666).
    if (!input.module_configuration_id) delete input.module_configuration_id;
    // Bypass fields skipped from the rate for configuration_id == 1 (legacy
    // js:1116-1121 / :1630-1637 — `configuration_id === 1 && (name.includes
    // ('bypass_') || name == 'dumper_supplied_by_id')`). The Bypass TAB is hidden
    // for config 1 OR 3, but only config 1 strips these from dataForRate; config 3
    // still sends the (default) bypass values.
    if (Number(s.configuration_id) === 1) {
      for (const k of Object.keys(input)) {
        if (k.startsWith('bypass_') || k === 'dmrs_supplied_by') delete input[k];
      }
    }
    return input;
  }, [phxDoc]);

  // ---- RATE (CODE-ONLY, userArmed) ----
  // `idx` mirrors legacy `rate(e, idx)` → `self.rated_phx_index = idx || 0`
  // (plate_heat_exchanger.selector.js:2687), i.e. the restored row index the
  // header-mode auto-re-fire passes in (`self.rate(null, model.rated_phx_index)`,
  // js:1460).
  // Set just before a re-rate the header popup runs by itself (restoring a
  // saved entry, or a manufacturer switch): that is not a user's rating, so
  // it is kept out of component_rated.
  const autoRateRef = useRef(false);
  const doRate = useCallback(async (idx = 0) => {
    const trackRating = !autoRateRef.current;
    autoRateRef.current = false;
    if (!requiredFilled) return;
    userArmed.current = true;
    setRateErrors([]);
    setRateWarnings([]);
    const input = buildRateInput();
    // ★ HEADER MODE: legacy's `dataForRate` IS the rate input and is what the
    // saved entry carries (js:1477). React builds the whole input in one pass
    // rather than accumulating it through `dataForRating`, so the ref is
    // refreshed here — a timing-only deviation; the persisted map is what the
    // legacy popup would restore into `self.dataForRate`.
    if (headerMode) dataForRateRef.current = input;
    const envelope = buildRatingEnvelope(
      'plate_heat_exchanger',
      state.current.mfg_id as any,
      input
    );
    const body = await dispatchRating(envelope, { track: trackRating });
    if (body.cancelled) return;
    // legacy `self.rated_datetime = self.generetionTimeStamp()` —
    // `YYYY.MM.DD_HH:mm:ss`, which is what every legacy-written
    // `ksdm_ahu_component_phx.rating_datetime` on dev3 holds. The port was
    // writing `"9/3/2026, 8:30:00 AM"` into that varchar. See ratingTimestamp.ts.
    ratedDatetime.current = generateRatingTimestamp();
    setRateVersion(body.version == null ? null : String(body.version));
    /**
     * legacy `self.from_api` (js:2856-2891) — set ONLY in the two branches under
     * `else` of `if (!ok)`, i.e. the vendor answered and said ok. A transport
     * failure (RTKQ surfaces one as `{ok:false, …}`) leaves it alone, which is
     * what keeps `notFound` — "the vendor found nothing" — from being claimed
     * for "the vendor never answered". dev renders an empty sheet in that case.
     */
    if (body.ok && !(body.errors ?? []).filter(Boolean).length) fromApi.current = true;
    // an error collapses the sheet onto Notes; otherwise the remembered tab (or
    // the input-pane default) stands (js:3037-3050).
    setOutputTab((body.errors ?? []).filter(Boolean).length ? 'notes' : outputTabRef.current);
    if (body.ok && body.models && body.models.length) {
      // keep only models with a truthy .model (legacy filter), nullify
      // recuperator-only outputs that came back undefined (Heatex strip).
      const rows = body.models
        .filter((r) => (r as any).model)
        .map((r) => {
          const row = { ...r } as Record<string, unknown>;
          PHX_FIELDS_TO_NULLIFY.forEach((f) => {
            if (row[f] === undefined) row[f] = null;
          });
          return row;
        });
      setRatedRows(rows);
      const chosen = rows[idx] ? idx : 0;
      setRatedIndex(chosen);
      savedRatedIndex.current = null;
      setRateWarnings((body.warnings ?? []).filter(Boolean));
      setSelectEnabled(rows.length > 0);
      // ★ HEADER MODE: the rate tail saves (js:2864-equivalent — the
      // render_outputs tail) so `rate:true` is stamped and the next popup
      // construction AUTO-RE-FIRES the rate.
      if (headerMode) saveInStorage({ rate: rows.length > 0, ratedIndex: chosen });
    } else {
      setRatedRows([]);
      setRateErrors((body.errors ?? []).filter(Boolean));
      setRateWarnings((body.warnings ?? []).filter(Boolean));
      // legacy `rate: !!self.rated_phxs` ⇒ false with no rows.
      if (headerMode) saveInStorage({ rate: false, ratedIndex: 0 });
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredFilled, buildRateInput, dispatchRating, setSelectEnabled, bump, headerMode, saveInStorage]);

  // ---- ★ Phase 6.5 — HEADER-MODE pending restore + AUTO-RE-FIRE -------------
  // Legacy hangs both off the DependenciesHandler `finished` callback
  // (plate_heat_exchanger.selector.js:791-797): once the cascade settles it either
  // re-fires the rate (`needToRate`) or re-applies the manufacturer's saved state
  // and rates (`findFromStoraga(needToSearch, true)` → js:1459-1461 `if
  // (model.rate && finish) self.rate(null, model.rated_phx_index)`). React has no
  // `finished` hook, so the cascade is settle-detected (isCascading false through
  // one 250 ms tick) — the pattern FanSelector established. NO dep array on
  // purpose: the effect re-arms every render, which is how a still-settling
  // cascade postpones itself.
  useEffect(() => {
    if (!headerMode) return undefined;
    const pending = pendingRestore.current;
    if (!pending) return undefined;
    if (!phxDoc) return undefined;
    if (!cascadeFired.current) return undefined;
    if (graph.isCascading) return undefined;
    const t = window.setTimeout(() => {
      if (graph.isCascading) return; // still settling — a later render re-arms
      if (pending.applyState) {
        // findFromStoraga js:1452-1457 — `{...default_values,
        // ...model.current_model, ...model.state, generic_mfg}`.
        state.current = {
          ...state.current,
          ...((pending.entry.current_model as Record<string, unknown>) ?? {}),
          ...((pending.entry.state as Record<string, unknown>) ?? {}),
          ...(pending.entry.generic_mfg != null ? { generic_mfg: pending.entry.generic_mfg } : {}),
        };
        savedRatedIndex.current = (pending.entry.rated_phx_index as number | null) ?? null;
        dataForRateRef.current = (pending.entry.dataForRate as Record<string, unknown>) ?? {};
        dataForSaveRef.current =
          (pending.entry.dataForSave as {
            modelField: Record<string, unknown>;
            searchField: Record<string, unknown>;
          }) ?? { modelField: {}, searchField: {} };
        pendingRestore.current = { entry: pending.entry, applyState: false };
        bump();
        return;
      }
      pendingRestore.current = null;
      // js:1459-1461 / js:791-793 — gated on `rate`, like ewheel.
      if (pending.entry.rate) {
        autoRateRef.current = true;
        void doRate(savedRatedIndex.current ?? 0);
      }
    }, 250);
    return () => window.clearTimeout(t);
  });

  // ---- SELECT (selectPhx — CODE-ONLY, userArmed) ----
  // ★ UNREACHABLE IN HEADER MODE: `#select` is never rendered
  // (component_selector.tmpl:131-136) and the `:selector:select` subscription is
  // omitted below, so none of the writes here — the __FORM_SHIM__ model.set,
  // `patchRatedPhx` (PATCH ahu/plate_heat_exchangers/rating_mode/<id>), the
  // catalogueFields resetFilter and `rating_mode:hide:<id>` — can ever run there.
  const getFormShim = (): ComponentFormShim | undefined => window.__FORM_SHIM__?.[modelId];

  // generic_mfg from the mfg select option's data-generic (legacy 1818-1824).
  // Both reads below are SCOPED to this selector's own chassis (see chassisEl) —
  // an un-scoped `#phx-selector …` read would find the board dialog's selector
  // when both hosts are mounted. Discovery §7.3 trap 1.
  const readGenericMfg = (): boolean => {
    const el =
      chassisEl()?.querySelector<HTMLSelectElement>('#phx-selector select[name="mfg_id"]') ?? null;
    const opt = el?.selectedOptions?.[0];
    const generic = opt?.dataset?.generic;
    return generic != null && generic !== 'none';
  };
  // shaft ids from the .shaft_* DOM datasets (legacy 1826-1828).
  const readShaftIds = (): Record<string, unknown> => {
    const r = (sel: string) =>
      chassisEl()?.querySelector<HTMLElement>(`#phx-selector ${sel}`)?.dataset?.id ?? null;
    return {
      dmr_shaft_shape_id: r('.shaft_shape'),
      dmr_shaft_dia_id: r('.shaft_diameter'),
      dmr_shaft_length_id: r('.shaft_length'),
    };
  };

  const doSelect = useCallback(() => {
    userArmed.current = true;
    if (!ratedRows || !ratedRows.length) {
      // never rated → no-op apply (legacy requires a rated row to select).
      finishSelect();
      return;
    }
    const r = ratedRows[ratedIndex] as Record<string, any>;
    const shim = getFormShim();
    const generic = readGenericMfg();
    const shaft = readShaftIds();
    const { modelsField, ratingFields, paramsFields } = buildSelectPayload(
      { ...state.current, __generic_mfg: generic, __rated_datetime: ratedDatetime.current, ...shaft },
      r,
      phxCatalogRef.current ?? {},
      dataForSaveRef.current.modelField,
      (detail ?? {}) as Record<string, any>
    );
    // Dev-sync J4 — the Checkpoint-4 switch (plate_heat_exchanger.selector
    // .js:2211-2218): POST plate_heat_exchangers/<id>/select with
    // models_field first; onCommit = commitPhxChain (:2203-2209) — the three
    // writes below. Board mode only; header keeps the plain sets. No
    // autoReload flag — a non-empty cascade hard-reloads (legacy default).
    // the rating_mode PATCH, captured so the reopen gate can wait on it: it is
    // the write that persists `model`, and `model` is what the stored-row seed
    // reads (see the seed effect).
    let ratedPatch: Promise<unknown> | null = null;
    const commitPhxChain = () => {
      // (1) current_model.set(models_field) → auto-save PATCH ahu/plate_heat_exchangers/<id>.
      shim?.model.set(modelsField);
      // (2) self.model.set(rating_fields) where model = rated_phx_model → PATCH
      //     ahu/plate_heat_exchangers/rating_mode/<id> (legacy self.model = the
      //     rated_phx_model option; the form passes it on the activate, but we also
      //     fire the PATCH directly — like coil's rated model — so the write is
      //     robust regardless of whether the form surfaced the model on the activate).
      const ratedModel = (activate as any).rated_phx_model as
        | { set?: (d: Record<string, unknown>) => void }
        | undefined;
      ratedModel?.set?.(ratingFields);
      ratedPatch = patchRatedPhx({ phxId: modelId, data: ratingFields });
      // (3) catalogueFields.master_phx_id.resetFilter(params_fields) (shim #6).
      shim?.catalogueFields?.master_phx_id?.resetFilter?.(paramsFields, false, 0);
      shim?.render();
      // (4) certification multiCheckbox commit-diff (downgrade fix): create the
      //     newly-checked certs, delete the unchecked ones, against the loaded
      //     baseline. Board mode only (the relation is /plate_heat_exchangers/<id>
      //     /certifications/, requires a real id — legacy js:939-953).
      for (const id of selectedCerts) {
        if (!certOriginal.current.has(id))
          void createTypeCertification({ plural: 'plate_heat_exchangers', id: modelId, certId: id });
      }
      for (const id of certOriginal.current) {
        if (!selectedCerts.has(id))
          void deleteTypeCertification({ plural: 'plate_heat_exchangers', id: modelId, certId: id });
      }
      certOriginal.current = new Set(selectedCerts);
    };
    if (!headerMode) {
      const cascade = guardSelect({
        url: `/api/v1.0/ahu/plate_heat_exchangers/${modelId}/select`,
        analyticsComponent: 'plate_heat_exchanger',
        payload: modelsField,
        onCommit: commitPhxChain,
      });
      // `finishSelect()` below closes the dialog on the CLICK (parity — legacy
      // fires `rating_mode:hide` before the save chain), so both writes run on
      // detached. A dialog reopened before they settle would GET a row that is
      // legitimately fresh and still carries no `model`. Make that reopen wait
      // for the writes instead (selectWriteGate.ts). `onCommit` runs BEFORE
      // guardSelect resolves (cascadeCommit.ts), so `ratedPatch` is already
      // assigned by the time the `.then` below reads it.
      const selectChain = cascade.then(() => ratedPatch ?? undefined).then(() => undefined);
      trackSelectWrites(selectWriteKey('plate_heat_exchangers', modelId), selectChain);
      void selectChain;
    } else {
      commitPhxChain();
    }
    finishSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratedRows, ratedIndex, modelId, activate, patchRatedPhx, headerMode, selectedCerts, createTypeCertification, deleteTypeCertification, detail]);

  const finishSelect = useCallback(() => {
    window.setTimeout(() => bus.emit('minRequiredLengthChange'), 1000);
    bus.emit('rating_mode:hide');
    bus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, bus]);

  // ---- RESET (`plate_heat_exchanger_<id>:selector:reset`) -------------------
  /**
   * Port of `reset` (plate_heat_exchanger.selector.js:1846-1884) — the #reset
   * circular-arrow button in the rating-dialog header, next to Print and RATE.
   * The chassis renders it (ComponentSelectorDialog) and fans it out on
   * `${dialog.name}_${dialog.modelId}:selector:reset`; legacy subscribes it at
   * js:930-934. It was never wired up in the React port — this is that wiring.
   *
   * LEGACY, in order:
   *   1. stash `mfg_id` (and the selected option's `data-generic`) — reset keeps
   *      the CURRENT manufacturer, it does not go back to Heatex;
   *   2. `self.rate_request?.abort()`;
   *   3. rebuild the params blob — legacy calls it `model_data
   *      .params_energy_wheel`, a copy-paste name from the energy-wheel file; it
   *      IS the PHX blob — as: every `<select class="search_field">` in the
   *      dialog blanked to null, then `...self.rated_default_values`, then five
   *      explicit pins, then the kept `mfg_id`. `self.state.set(...)` it;
   *   4. re-select the kept mfg `<option>` and `$select.trigger('change')`.
   *
   * ★★ STEP 4 IS THE WORKHOUSE, and the only interesting mapping here. The
   * trigger runs `changeSearchField` (js:2448-2512) for name `mfg_id`, whose
   * effects — and their React equivalents, applied below in the same order —
   * are:
   *   `updateDefaultsDependsOnMFG(value)` (:2487) → the PHX_MFG_DEFAULTS merge.
   *   `showHideInputsDependsOnMfg(value)` (:2488) → nothing to do: the
   *       show-for-heatex / hide-for-heatex toggle is declarative in React (the
   *       `isHeatex` branches at render time), so `bump()` covers it.
   *   `dataForSave.searchField[name] = value` (:2497) → header-mode only ref.
   *   `dataForRating(value, name)` (:2501) → nothing to do: React builds the
   *       whole rate input in one pass in `buildRateInput()` rather than
   *       accumulating it, so `dataForRate` has no incremental state to poke
   *       (the same deviation already documented on doRate).
   *   `checkRequiredFields()` (:2502) → nothing to do: the `requiredFilled` memo
   *       and the `#rate` visual-gate effect both recompute off `state.current`
   *       on the re-render `bump()` schedules.
   *   `$el.find('.output-part')[0].innerHTML = ''` (:2503) → setRatedRows(null)
   *       plus clearing the error/warning lists and the rated timestamp: in
   *       React those three are separate pieces of the one `.output-part` node
   *       (see the RatedOutputsTable render site), so blanking the node means
   *       blanking all of them. THE RATED ROWS DO NOT SURVIVE A RESET.
   *   `self.rate_request?.abort()` (:2504) → `abortRating()` — the same in-flight
   *       abort `:selector:cancel` uses. (Legacy aborts twice: once in `reset`
   *       itself at :1850 and again here. Once is enough.)
   *   `dependenciesHandler.loadOptionsData(name)` (:2505) → ★
   *       `graph.onParentChange('mfg_id')`. This is the exact React counterpart:
   *       both walk the mfg node's descendant layers, re-fetch each dependent
   *       option list narrowed by the (now-reset) parents, and re-pick each
   *       field's value — which is what repopulates series/size/seal/plate/
   *       end-plate/corner-profile/… after the blanking above. Fired LAST, as
   *       legacy does, so the cascade's re-picks win over the blob.
   *
   * ★ NOT PORTED — `findFromStoraga(self.mfg_id)` (:2509), the header-only tail
   * of the mfg_id branch. It would immediately restore that manufacturer's saved
   * localStorage entry and thereby UNDO the reset, and (via `model.rate`)
   * re-fire an external vendor rate. Unreachable in practice: the header host
   * renders with `show_tab:'all'`, which matches none of the legacy `#reset`
   * branches, so the header popup has no Reset button to press. Header handling
   * below is therefore defensive only. Note legacy's mfg_id branch also does NOT
   * `saveInStorage()` (:2512 is the `else`), so neither do we.
   * ★ NOT PORTED — the DOM `option.prop('selected')` re-selection (:1871-1881).
   *   React's selects are controlled off `state.current`; writing `mfg_id` back
   *   IS the re-selection. `data-generic` is likewise read at render.
   * ★ NOT TOUCHED — `init.current`. It is the pristine as-loaded snapshot and a
   *   reset is a user edit, not a fresh load. (It is currently write-only in this
   *   file anyway.)
   */
  const doReset = useCallback(() => {
    // (1) the manufacturer is KEPT (js:1848).
    const mfgId = state.current.mfg_id;
    // (2) js:1850.
    abortRating();

    // (3) js:1852-1868 — blank every search_field SELECT, then the rated
    //     defaults, then the five pins, then the kept mfg_id.
    const blanked: Record<string, unknown> = {};
    for (const name of PHX_RESET_BLANKED_SELECTS) blanked[name] = null;
    const next: Record<string, unknown> = {
      ...state.current,
      ...blanked,
      ...PHX_RATED_DEFAULT_VALUES,
      // js:880 — the cap is grafted onto rated_default_values once mfg-settings
      // lands, so it only participates in the reset when it has been loaded.
      ...(maxAllowedApdRef.current != null ? { max_allowed_dp: maxAllowedApdRef.current } : {}),
      // the five explicit pins (js:1862-1866).
      type_desired_efficiency_id: 3,
      configuration_id: 3,
      face_one_definition_id: 2,
      face_two_definition_id: 1,
      plate_orientation_id: 1,
      mfg_id: mfgId,
    };

    // (4a) `$select.trigger('change')` → updateDefaultsDependsOnMFG (js:2487 →
    //      :2759-2808). `preserveExisting` is falsy on this path, so the
    //      master_phx / end_plate_style_id preservation branch (:2820-2829) does
    //      not run. Applied AFTER the blob, exactly as legacy orders it — which
    //      is how mfg 167 ends up at configuration_id 2 rather than the pin's 3.
    const mfgDefaults = mfgId == null ? undefined : PHX_MFG_DEFAULTS[Number(mfgId)];
    if (mfgDefaults) Object.assign(next, mfgDefaults);
    state.current = next;

    // (4b) header-mode bookkeeping (js:2497 / :2812). `dataForSave` is only ever
    //      persisted in header mode in this port, so it is written under the same
    //      guard the surrounding change handlers use.
    if (headerMode) {
      dataForSaveRef.current.searchField.mfg_id = mfgId;
      if (mfgDefaults) Object.assign(dataForSaveRef.current.modelField, mfgDefaults);
      phxMfgIdRef.current = mfgId ?? null;
    }

    // (4c) `.output-part` blanked (js:2503).
    setRatedRows(null);
    setRatedIndex(0);
    setRateErrors([]);
    setRateWarnings([]);
    setOutputTab(null);
    setRateVersion(null);
    fromApi.current = false;
    ratedDatetime.current = null;
    // NB `setSelectEnabled` is intentionally NOT called: the existing
    // detail/ratedRows effect recomputes it from `setRatedRows(null)` and would
    // override anything set here anyway.

    markChanged();
    bump();

    // (4d) `dependenciesHandler.loadOptionsData('mfg_id')` (js:2505). Fired last,
    //      like legacy — the cascade's re-picks land on top of the blob.
    if (mfgId != null) graph.onParentChange('mfg_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, abortRating, markChanged, bump, headerMode]);

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      bus.on(`${eventName}_${scope}:selector:rate` as any, () => void doRate()),
      // ★ HEADER MODE omits `:selector:select` entirely. `.control-buttons`
      // (#select + #cancel) is wrapped in `{{#ifCompare show_tab '!=' 'all'}}`
      // (component_selector.tmpl:131-136), so the header chassis can never
      // dispatch it — legacy subscribes anyway
      // (plate_heat_exchanger.selector.js:855-859) but the handler is
      // unreachable. Not subscribing makes header mode's ZERO-WRITES property
      // structural rather than incidental (discovery §3.1).
      ...(headerMode
        ? []
        : [bus.on(`${eventName}_${scope}:selector:select` as any, () => doSelect())]),
      bus.on(`${eventName}_${scope}:selector:cancel` as any, () => abortRating()),
      // #reset (plate_heat_exchanger.selector.js:930-934). Subscribed in BOTH
      // hosts, as legacy does — but only the board chassis renders the button
      // (the header host uses show_tab:'all'), so header mode is defensive only.
      // `doReset` is deliberately absent from the dep array: it closes over
      // `graph`, whose identity changes every render, so listing it would tear
      // down and re-add every listener on every render. It reads all mutable
      // state through refs, so the once-captured closure is safe — the same
      // arrangement FanSelector.tsx uses for its own reset.
      bus.on(`${eventName}_${scope}:selector:reset` as any, () => doReset()),
      bus.on('cancel:dialog:changes', () => abortRating()),
      // NOTE no `:selector:print` subscription in EITHER host: print is
      // SUPPRESSED for plate_heat_exchanger in header mode — the chassis stamps
      // `hide-print` on `.ahu-rate-content` (component_selector.js:175/:183) and
      // `.ahu-rate-content.hide-print #print{opacity:0;pointer-events:none}`
      // (common.css:12593-12596) makes the icon unclickable (observed live).
      // Legacy DOES subscribe it (js:850-854) and even un-`non-clickable`s
      // `#print` at js:3212-3218 whenever `self.current_model` is truthy (always,
      // in header mode, because it is synthesized) — but `hide-print` wins, so the
      // handler is unreachable.
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, scope, bus, headerMode, doRate, doSelect, abortRating]);

  // abort any in-flight rate on unmount (data-safety).
  useEffect(() => () => abortRating(), [abortRating]);

  /**
   * `checkOutputPerformanceTab` + `syncInputPerformanceTab` (js:3094-3130).
   * The output sheet only draws the season columns whose RATE INPUT airflow is
   * > 0, and when only one of them qualifies legacy collapses BOTH panes onto
   * it — the summer/winter strips are the same `.summer-winter-tabing` class and
   * the click handler is document-wide, so input and output always agree.
   * Runs on the render_outputs boundary (a rate, or the initial saved-row draw).
   */
  useEffect(() => {
    if (ratedRows == null) return;
    const winter = Number(state.current.oa_airflow_actual_winter ?? 0) > 0;
    const summer = Number(state.current.oa_airflow_actual_summer ?? 0) > 0;
    if (winter && !summer) setSeason('winter');
    else if (summer && !winter) setSeason('summer');
  }, [ratedRows]);

  // Bypass tab auto-hides for configuration_id 1/3 (toggleBypassVisibility,
  // js:2742-2757). If the user is ON the Bypass tab when config flips to 1/3, fall
  // back to Properties so the content pane isn't left blank. Runs every render;
  // settles in one tick (only sets when actually on a now-hidden Bypass tab).
  useEffect(() => {
    const cfg = Number(state.current.configuration_id);
    if (activeTab === 'Bypass' && (cfg === 1 || cfg === 3)) setActiveTab('Properties');
  });

  /**
   * ★ THE MANUFACTURER LIST IS FETCHED, NOT HARDCODED (js:968-991).
   *
   * The port rendered two literal `<option>`s (Heatex 52, Recuperator 167) and
   * pinned `mfg_id: 52`. dev reads the list off the phx CMS params doc and picks
   *   `manufacturer.find(e => e.id == 52) || manufacturer[0]`
   * — Heatex if the catalogue offers it, otherwise WHATEVER IS FIRST.
   *
   * On dev3 that list is Recuperator only (167, plus its `generic_mfg` twin
   * "Thermal Corporation"); 52 is not in it. So dev opens this dialog on
   * Recuperator while the port opened it on a manufacturer the catalogue does
   * not offer — which is what made the rest of the dialog look wrong. WB
   * Temperature and Pressure In are `show-for-heatex` rows; Series|Shape, Bank
   * Wide x High and Face Definition 1/2 are `hide-for-heatex`, so every one of
   * them sat on the wrong side. Heatex also pins `configuration_id` to 3, one of
   * the two values toggleBypassVisibility hides the Bypass tab for — hence "the
   * Bypass page is missing".
   *
   * Same defect and same fix shape as the energy wheel's
   * `find(233) || manufacturer[0]` (PR #1107); the tracker's Phase-6.5 residual
   * item (2) records that PHX was not covered by that pass.
   *
   * Applied once the doc lands (js:984-990 re-pins mfg/series whenever no rated
   * `model` is chosen). Guarded so it only corrects a manufacturer the catalogue
   * does NOT offer: a saved unit, a restored header entry and any user choice
   * keep theirs, because those ids ARE in the list.
   */
  const mfgResolved = useRef<number | null>(null);
  useEffect(() => {
    const { list, defaultMfgId } = resolvePhxMfg(phxDoc);
    if (!list.length || defaultMfgId == null) return;
    if (seeded.current !== modelId || mfgResolved.current === modelId) return;
    const cur = state.current.mfg_id;
    mfgResolved.current = modelId;
    if (state.current.model) return;
    if (cur != null && list.some((m) => Number(m.id) === Number(cur))) return;
    state.current = {
      ...state.current,
      mfg_id: defaultMfgId,
      series_id: PHX_MFG_DEFAULT_SERIES[Number(defaultMfgId)] ?? null,
      ...(PHX_MFG_DEFAULTS[Number(defaultMfgId)] ?? {}),
    };
    // ★ Bank Wide x High read "1 x 1" where dev reads blank. `setBankWideByHigh`
    // (js:1670-1701) derives the bank geometry FROM the calculation mode, and
    // mode 1 — Recuperator's default — blanks wide/high and pins deep to 1.
    // The port only ran it from the calc_mode_id change handler, so the seed's
    // literal `bank_wide: 1, bank_high: 1` (default_values, js:362-364) survived
    // on open. Legacy re-derives it whenever the mfg defaults land.
    applyCalcMode(state.current, num(String(state.current.calc_mode_id ?? '')));
    if (headerMode) {
      phxMfgIdRef.current = defaultMfgId;
      Object.assign(dataForSaveRef.current.modelField,
        PHX_MFG_DEFAULTS[Number(defaultMfgId)] ?? {});
    }
    graph.onParentChange('mfg_id');
    bump();
    // ★ `seedTick` for the same reason as the mount cascade above — this runs
    // once per open and must run AFTER the seed, which the gate can defer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phxDoc, modelId, headerMode, seedTick]);

  // ---- render ----
  if (detailLoading || phxDocLoading || airStreamsQ.isLoading || seeded.current !== modelId) {
    return <div className="preloader dotted-preloader active" />;
  }

  const s = state.current;
  const mfg = Number(s.mfg_id);
  const isHeatex = mfg == 52;
  // the fetched manufacturer list + dev's `find(52) || [0]` pick — see
  // resolvePhxMfg (module scope) for why this is not the hardcoded pair.
  const { list: cmsManufacturers } = resolvePhxMfg(phxDoc);
  const streams = (airStreamsQ.data as OptionRow[] | undefined) ?? [];
  const pricingMethods = (pricingMethodsQ.data as OptionRow[] | undefined) ?? [];
  const masterPhx = ((detail as PhxModel)?.master_phx ?? {}) as Record<string, unknown>;


  // legacy pane class lists differ per tab and the port had flattened them:
  // Properties is the only one WITHOUT `col-xs-12` (tmpl:27), Pricing the only one
  // without `float-left` (tmpl:1160), the other three carry both (tmpl:474, 729, 1068).
  const tabClass = (t: InputTab, extra = 'col-xs-12 float-left') =>
    `tab-pane fade ${extra} scrollable-content${activeTab === t ? ' in active' : ''}`;

  const subPane = (isActive: boolean) =>
    `tab-pane fade scrollable-content col-xs-12 float-left${isActive ? ' in active' : ''}`;

  // ----- derived gating -----
  // bank wide×high editable only for calc_mode 2 (legacy setBankWideByHigh adds
  // .non-clickable for modes 1 & 3, js:1670-1701).
  const bankEditable = Number(s.calc_mode_id) === 2;
  // Altitude is the PROJECT's elevation, read-only, defaulting to 0
  // (tmpl:903 `{{#if project.elevation}}{{project.elevation}}{{else}}0{{/if}}`).
  // `||`, not `??`: the legacy `{{#if}}` is falsy-to-zero, so an empty string or a
  // 0 both read "0", and the header host — which is handed no project at all —
  // reads "0" as well.
  const projectElevation = String(elevationRef.current || 0);
  // Bypass Damper / Recirculation sub-tabs go `.non-clickable` when the bypass
  // type is 1 — legacy changeSearchField's bypass_type_id branch (js:2305-2317)
  // toggles the class on `.bypass_more` / `.recirculation_more` and their `<a>`s.
  const bypassSubTabsLocked = Number(s.bypass_type_id) === 1;
  // Bypass tab hidden for configuration_id 1 or 3 (legacy toggleBypassVisibility,
  // js:2742-2757; tmpl:13-14 hidden classes). Default config 3 → hidden.
  const configId = Number(s.configuration_id ?? masterPhx.configuration_id);
  const bypassHidden = configId === 1 || configId === 3;
  // the visible top-level tabs (Bypass dropped when hidden).
  const inputTabs = (['Properties', 'Performance', 'Bypass', 'Settings', 'Pricing'] as InputTab[])
    .filter((t) => !(t === 'Bypass' && bypassHidden));
  const tabLabel = (t: InputTab): string => (t === 'Properties' ? 'Heat-Exchanger' : t);
  // pricing input row shows for method != 1 and != null (legacy js:1608). method 2
  // → user COST; any other non-1 method → LIST price_* (pricing_method_inputs.tmpl).
  const pmId = s.pricing_method_id == null ? null : Number(s.pricing_method_id);
  const showPricingInputs = pmId != null && pmId !== 1;

  // ----- the rated-outputs sheet -----
  const rows = ratedRows ?? [];
  // legacy's `{{#ifCompare ok 'or' rated_phx}}` (rated.outputs.tmpl:28): a rate has
  // produced rows, or produced a message. Nothing renders under the input pane
  // before that — `render_outputs` is only reached after a rate (js:1645).
  const hasRated = ratedRows != null || rateErrors.length > 0 || rateWarnings.length > 0;
  /**
   * ★ Mfg's Notes are per-MANUFACTURER. The endpoint returns every PHX vendor's
   * notes and legacy filters at render time —
   * `self.notes.toJSON().filter(n => n.component_mfg_id == self.state.get('mfg_id'))`
   * (js:3007), the same thing EnergyWheelRatedOutputs does. The port passed the
   * list unfiltered, so a Heatex selection showed Recuperator's note.
   */
  const mfgNotes = ((notesQ.data ?? []) as Array<{ note: string; component_mfg_id?: number | null }>)
    .filter((n) => (mfg == null || n.component_mfg_id == null ? true : n.component_mfg_id == mfg));

  // ---- the context `render_outputs` hands the output template (js:2999-3026) --
  /** legacy `rated_phx` after its fallback (js:3011): the picked candidate when
   *  it has keys, otherwise the SAVED component row. */
  const ratedPhx = (rows[ratedIndex] ?? (detail as Record<string, unknown> | undefined) ?? {}) as Record<
    string,
    unknown
  >;
  /**
   * legacy `dataForRate` — the accumulated rate input. The output pane reads six
   * keys off it (the four per-season airflows plus bank_wide/bank_high) and they
   * all come straight from the input fields, so the live state IS the value on
   * the board. Header mode keeps its own restored copy (js:451/464), so that one
   * wins when it is populated.
   */
  const outputDataForRate: Record<string, unknown> = {
    oa_airflow_actual_winter: s.oa_airflow_actual_winter,
    ra_airflow_actual_winter: s.ra_airflow_actual_winter,
    oa_airflow_actual_summer: s.oa_airflow_actual_summer,
    ra_airflow_actual_summer: s.ra_airflow_actual_summer,
    bank_wide: s.bank_wide,
    bank_high: s.bank_high,
    ...dataForRateRef.current,
  };
  /** legacy `notFound` (js:2962) — `ok && from_api && !Object.keys(rated_phx)`,
   *  i.e. the vendor answered cleanly with no candidate at all. */
  const notFound = fromApi.current && rows.length === 0 && rateErrors.length === 0;
  /**
   * legacy `splitedNotes` (js:2930-2960): with no candidate it is the SAVED
   * `rating_notes` string split on `;` then `:`; otherwise the candidate's own
   * `errors` map. Rendered as the Notes tab's "Warning :" block.
   */
  const splitedNotes: Record<string, unknown> | null = (() => {
    if (!rows.length) {
      const txt = (detail as Record<string, unknown> | undefined)?.rating_notes;
      if (typeof txt !== 'string' || !txt) return null;
      const out: Record<string, unknown> = {};
      for (const part of txt.split(';')) {
        if (!part) continue;
        const [k, v] = part.split(':');
        if (v !== 'null') out[k] = v;
      }
      return Object.keys(out).length ? out : null;
    }
    const errs = ratedPhx.errors;
    if (!errs || typeof errs !== 'object') return null;
    // legacy drops the map when EVERY value is null (`Object.values().isNull()`).
    const vals = Object.values(errs as Record<string, unknown>);
    if (!vals.length || vals.every((v) => v == null)) return null;
    return errs as Record<string, unknown>;
  })();
  /** the output tab's default follows the INPUT pane's (js:3052-3060). */
  const effectiveOutputTab: PhxOutputTab =
    outputTab ?? (activeTab === 'Performance' ? 'performance' : 'properties');

  // everything the module-scope sub-views close over. Deliberately NOT memoised:
  // `state.current` is replaced wholesale and only `bump()` announces it, so a new
  // object per render is what keeps the consumers current. Re-rendering them is
  // free; it is their *identity* that must not move (see the PhxViewCtx note).
  const phxCtx: PhxViewCtx = {
    s, season, isHeatex, streams, masterLists, phxDoc, detail, masterPhx, projectElevation,
    pmId, selectedCerts, certListOpen, setCertListOpen, toggleCertification,
    onSearchField, onModelField,
  };

  return (
    // ★ JOB B — LEGACY DOM RESTORE. The Backbone view el is
    // `<div class="col-xs-12 plate-htex-col">` (plate_heat_exchanger.selector.js
    // :177) wrapping the template's TWO siblings: the input pane
    // `#plate_heat_exchanger` (tmpl:2) and the rated-output block
    // `.plate_heat_exchanger.output-part` (tmpl:1190). The React port had collapsed
    // these into one `#phx-selector` div with the output NESTED inside, so the CSS
    // stopped matching: `#rating_mode_plate_heat_exchanger_content > .col-xs-12`
    // (height 100%, common.css:20801), `#plate_heat_exchanger {height:320px}`
    // (:20870 — the input-pane cap that stopped the rated table overlapping) and
    // `> .col-xs-12 .plate_heat_exchanger {height:calc(100% - 320px)}` (:20804 —
    // the output block). Restoring wrapper > input-pane + sibling-output re-arms
    // them. `#phx-selector` stays on the wrapper (readGenericMfg/readShaftIds read
    // `#phx-selector …`); the input pane carries the legacy id/classes.
    //
    // The context provider is a wrapper with no DOM of its own, so the element
    // tree below is unchanged.
    <PhxCtx.Provider value={phxCtx}>
    <div ref={rootRef} id="phx-selector" className="col-xs-12 plate-htex-col">
      {/* legacy start preloader (tmpl:1) — DOM parity; not auto-active in React. */}
      <div className="plate-heatex-start-preloader preloader dotted-preloader" />

      {/* ===== INPUT PANE (#plate_heat_exchanger) — a CHILD of .plate-htex-col ===== */}
      <div id="plate_heat_exchanger"
        className={`input-part plate-heat-exchanger component-rating-mode${ratedRows?.length ? ' rating' : ''}`}>
        {/* available-sizes wrap kept for the section-resize repaint hook */}
        <div className="section-available-sizes-wrap" />

        {/* input tab nav — the legacy 5 top-level tabs (tmpl:3-24); Bypass dropped
            for config 1/3. The `.row` and the `ul.col-xs-12` are legacy's
            (tmpl:4-5): the row's -15px margins cancel the ul's column padding, and
            `input-menu` belongs on the UL, not on the outer nav div. */}
        <div className="col-xs-1 nav nav-tabs">
          <div className="row">
            <ul className="col-xs-12 input-menu">
              <li className="cursor-default">Input</li>
              {inputTabs.map((t) => (
                <li key={t} className={`input_label ${t}${activeTab === t ? ' selected active' : ''}`}>
                  <a tabIndex={-1} className={activeTab === t ? 'active' : undefined}
                    onClick={() => setActiveTab(t)}>{tabLabel(t)}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="col-xs-11 content-data tab-content">
          {/* tmpl:26 — ONE `.row` wrapping all five panes. Load-bearing, not
              decoration: every pane is `col-xs-12` (padding 0 15px) and this row's
              `margin: 0 -15px` is the only thing cancelling that gutter. Without it
              each sub-page draws 15px right of dev's and 30px narrower. (Same
              defect as the coil dialog, PR #1168.) */}
          <div className="row">
          {/* ----- Properties (Heat-Exchanger) — 4 sub-tabs (tmpl:27-473) ----- */}
          <div className={tabClass('Properties')} id={`${idPrefix}Properties`}>
            <SubTabNav
              tabs={[
                { key: 'general' as PropSubTab, paneId: 'properties_more', hook: 'properties-tab', label: 'General', pad: '\u00a0' },
                { key: 'internal_plates' as PropSubTab, paneId: 'internal_plates_more', label: 'Internal Plates', pad: '\u00a0' },
                { key: 'end_plates' as PropSubTab, paneId: 'end_plates_more', label: 'End Plates', pad: '\u00a0' },
                { key: 'corner_profiles' as PropSubTab, paneId: 'corner_profiles_more', label: 'Corner Profiles' },
              ]}
              active={propSubTab}
              onPick={setPropSubTab}
            />
            <div className="more-tab-content">
              {/* General (tmpl:49-296) */}
              <div className={subPane(propSubTab === 'general')} id="properties_more">
                {/* tmpl:50-161 — the LEFT column's rows live inside
                    `.row > .col-xs-9.first_column_input`. That wrapper is what
                    common.css:20970-20979 hangs the left column's geometry off
                    (`.first_column_input .input-part {padding-left:22px}`,
                    `.first_column_input .label-part {padding-right:7px}` and the
                    `… .input-part .col-xs-12 select {width: calc(151% - 5px)}`
                    overhang). Without it the selects fall back to 100% and the
                    column reads noticeably narrower than dev's. */}
                <div className="col-xs-12 col-md-12 col-lg-6 input-part-left">
                  <div className="row">
                    <div className="col-xs-9 first_column_input">
                      {/* mfg is a 2-option search select (Heatex/Recuperator); data-generic
                          feeds readGenericMfg (legacy js:1818-1824). */}
                      <SelectRow label="Manufacturer" dataName="mfg_id">
                        {/* options come from the CMS doc (tmpl:63-69,
                            `{{#each collections.manufacturer}}`), each row carrying
                            its `generic_mfg` on `data-generic` for readGenericMfg
                            (js:1818-1824). */}
                        <select name="mfg_id" data-name="mfg" id="mfg_id" className="search_field" value={s.mfg_id == null ? '' : String(s.mfg_id)}
                          onChange={(e) => onSearchField('mfg_id', e.target.value)} data-generic={String((masterPhx as any).generic_mfg ?? 'none')}>
                          {cmsManufacturers.map((m) => (
                            <option key={String(m._id ?? m.id)} value={String(m.id)}
                              data-generic={m.generic_mfg ? String(m.generic_mfg) : 'none'}>
                              {String(m.name ?? '')}
                            </option>
                          ))}
                        </select>
                      </SelectRow>
                      {/* ★ DOWNGRADE FIX: series is now an EDITABLE dependency select. */}
                      <SeriesSelect />
                      {/* ★ RESTORED: size (getSize, catalogue filter) + module configuration
                          (getModuleConfigurations, ★ rate field). */}
                      <OptionSelect name="size_id" label="Size" options={sizes} search />
                      {/* `show-tooltip config-text-above` is legacy's (tmpl:113); the
                          -25px width variant at common.css:20979 keys off the name. */}
                      <OptionSelect name="module_configuration_id" label="Module Configuration"
                        options={moduleConfigs} search selectClass="search_field show-tooltip config-text-above" />
                      <DocSelect name="flow_type_id" label="Flow Type" />
                      {/* tmpl:136 is `<span class="right temp uom">F</span>` — the
                          degree glyph comes from `.temp:before {content:"o"}`
                          (common.css:20928/:21834), NOT from the unit string. The
                          unit must stay "F" or the label reads "ᵒoF". Same pairing
                          as the Performance DB/WB rows (unit="F" unitTemp). */}
                      <MasterSelect name="temp_rating_id" label="Temperature Rating" unit="F" unitTemp labelKey="name" />
                      <MasterSelect name="seal_id" label="Seal" />
                    </div>
                  </div>
                </div>
                {/* tmpl:163-295 — the RIGHT column has NO first_column_input; its rows
                    sit straight in the column. */}
                <div className="col-xs-12 col-md-12 col-lg-6 input-part-right">
                  {/* ★ RESTORED: total width/height max (from section aw_width/aw_height). */}
                  <Num name="total_width_max" label="Total Width Max" unit="in" model={false} textRight />
                  <Num name="total_height_max" label="Total Height Max" unit="in" model={false} textRight />
                  <MasterSelect name="mounting_id" label="Mounting" />
                  <MasterSelect name="configuration_id" label="Configuration" />
                  {/* Bank Wide x High (tmpl:222-241) — two inputs in a
                      `col-xs-5 / col-xs-2 / col-xs-5` split with the X in the middle
                      cell. The port had put all three inputs inside a bare <span>;
                      `.component-rating-mode input {width:100%}` (common.css:20996)
                      then forced each one onto its own line, which is the stacked
                      "1 / X / 1 / X / 1" in the report. Editable only for calc_mode 2
                      (setBankWideByHigh); hide-for-heatex (tmpl:222). */}
                  {!isHeatex && (
                    <SplitRow
                      rowClass="bank_wide_bank_high"
                      label={
                        /* tmpl:223-226 verbatim — two spans with a bare " x " between
                           them. The spaces are explicit because JSX would drop them. */
                        <>
                          <span data-name="bank_wide">Bank Wide{' '}</span>
                          {' x '}
                          <span data-name="bank_high">High</span>
                        </>
                      }
                    >
                      <div className="col-xs-5 no-left-padding">
                        <PhxNumberInput name="bank_wide" value={s.bank_wide} formatter="integer"
                          className={`model_field bank_wide${bankEditable ? '' : ' non-clickable'}`}
                          readOnly={!bankEditable}
                          onCommit={(raw) => onModelField('bank_wide', raw)} />
                      </div>
                      <div className="col-xs-2 padding-top-9">
                        <span className="unit not-uom ">X</span>
                      </div>
                      <div className="col-xs-5">
                        <PhxNumberInput name="bank_high" value={s.bank_high} formatter="integer"
                          className={`model_field bank_high${bankEditable ? '' : ' non-clickable'}`}
                          readOnly={!bankEditable}
                          onCommit={(raw) => onModelField('bank_high', raw)} />
                      </div>
                    </SplitRow>
                  )}
                  {/* ▲ REMOVED (QA 2026-09-07): Bank Deep, Casing Width and Height
                      from Floor were rows the port invented. dev's template has no
                      casing_width / height_from_floor control at all and its Bank
                      Deep select is commented out (tmpl:243-266). The columns still
                      exist and still round-trip — `bank_deep` is derived by
                      setBankWideByHigh — they simply have no UI, as on dev. */}
                  {/* Face Definition 1/2 (tmpl:268-292) — ONE row, two selects in a
                      `col-xs-6 / col-xs-6` split, not two rows. */}
                  {!isHeatex && (
                    <SplitRow label="Face Definition 1/2" dataName="face_one_definition_id" rowClass="hide-for-heatex">
                      <div className="col-xs-6 no-left-padding">
                        <FaceSelect name="face_one_definition_id" />
                      </div>
                      <div className="col-xs-6">
                        <FaceSelect name="face_two_definition_id" />
                      </div>
                    </SplitRow>
                  )}
                </div>
              </div>
              {/* Internal Plates (tmpl:297-372) — note this pane's split is 4/8, not
                  6/6, and its wrapper is col-xs-10 (which is why common.css:20982
                  sizes its selects at 102% where the other panes get 151%). */}
              <div className={subPane(propSubTab === 'internal_plates')} id="internal_plates_more">
                <div className="col-xs-12 col-md-12 col-lg-6">
                  <SubTitle>Internal Plates</SubTitle>
                  <div className="row">
                    <div className="col-xs-10 first_column_input">
                      <DocSelect name="plate_spacing_code_id" label="Spacing" labelCols={4} />
                      <MasterSelect name="plate_mtl_id" label="Material" labelCols={4} />
                      {/* ★ RESTORED: plate_pattern (REQUIRED, ★ rate field). */}
                      <MasterSelect name="plate_pattern_id" label="Pattern" labelCols={4} />
                      <MasterSelect name="plate_finish_id" label="Finish" labelCols={4} />
                      <MasterSelect name="plate_orientation_id" label="Orientation" labelCols={4} />
                    </div>
                  </div>
                </div>
              </div>
              {/* End Plates (tmpl:373-422) */}
              <div className={subPane(propSubTab === 'end_plates')} id="end_plates_more">
                <div className="col-xs-12 col-md-12 col-lg-6">
                  <SubTitle>End Plates</SubTitle>
                  <div className="row">
                    <div className="col-xs-9 first_column_input">
                      <MasterSelect name="end_plate_style_id" label="Style" />
                      <MasterSelect name="end_plate_mtl_id" label="Material" />
                      <MasterSelect name="end_plate_finish_id" label="Finish" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Corner Profiles (tmpl:423-471) */}
              <div className={subPane(propSubTab === 'corner_profiles')} id="corner_profiles_more">
                <div className="col-xs-12 col-md-12 col-lg-6">
                  <SubTitle>Corner Profiles</SubTitle>
                  <div className="row">
                    <div className="col-xs-9 first_column_input">
                      <MasterSelect name="corner_profile_style_id" label="Style" />
                      <MasterSelect name="corner_profile_mtl_id" label="Material" />
                      <MasterSelect name="corner_profile_finish_id" label="Finish" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ----- Performance (winter/summer tabs, tmpl:729-1067) ----- */}
          <div className={`${tabClass('Performance')} Performance`} id={`${idPrefix}Performance`}>
            {/* season nav (tmpl:730-742). The port had a bare `ul.list-inline`; dev
                wraps it in the same `col-xs-12 sub-tab-menu nav nav-tabs > .row >
                ul.col-xs-12 text-right` as the other sub-navs, which is what
                right-aligns it and gives `.nav-tabs ul .winter-tab a` its 10px
                padding (common.css:20903). */}
            <div className="col-xs-12 sub-tab-menu nav nav-tabs">
              <div className="row">
                <ul className="col-xs-12 text-right performance_tabing summer-winter-tabing">
                  <li className={`winter winter-tab${season === 'winter' ? ' active' : ''}`} data-tab="winter">
                    <a tabIndex={-1} data-tab="winter" onClick={() => setSeason('winter')}>{'winter \u00a0'}</a>
                  </li>
                  <li className={`summer summer-tab${season === 'summer' ? ' active' : ''}`} data-tab="summer">
                    <a tabIndex={-1} data-tab="summer" onClick={() => setSeason('summer')}>summer</a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="performance-tab-content">
              {/* ★ NO "SP Outside" row. `oa_sp` is a real writable column and is
                  still sent (see `fb('oa_sp')` in the Select payload), but dev
                  renders no control for it anywhere in
                  plate_heat_exchanger.selector.tmpl — it is model-only. */}
              <SeasonBlock sea="winter" />
              <SeasonBlock sea="summer" />
            </div>
          </div>

          {/* ----- Bypass (3 sub-tabs; hidden for config 1/3, tmpl:474-728) ----- */}
          {!bypassHidden && (
            <div className={tabClass('Bypass')} id={`${idPrefix}Bypass`}>
              {/* tmpl:475-489. Three things this nav does NOT share with the
                  Properties one: `bypass-tabs` on the wrapper (`.nav-tabs.bypass-tabs
                  {z-index:100}`, common.css:20898), no `.col-xs-12` between the row
                  and the ul, and an inline `style="top:0"` on THIS ul only — which
                  is what cancels `.more_tabing {position:absolute; top:-41px}`
                  (:21204) for the Bypass bar while leaving the Properties bar
                  lifted. The two panes are NOT symmetric, and the scss override must
                  not treat them as such. Its labels also carry a space BEFORE the
                  nbsp, where the Properties nav's do not. */}
              <SubTabNav
                navClass="bypass-tabs"
                wrapCol={false}
                tabs={[
                  { key: 'general' as BypassSubTab, paneId: 'general_more', label: 'General', pad: ' \u00a0' },
                  { key: 'bypass' as BypassSubTab, paneId: 'bypass_more', label: 'Bypass Damper', pad: ' \u00a0', locked: bypassSubTabsLocked },
                  { key: 'recirculation' as BypassSubTab, paneId: 'recirculation_more', label: 'Recirculation', locked: bypassSubTabsLocked },
                ]}
                active={bypassSubTab}
                onPick={setBypassSubTab}
                ulStyle={{ top: 0 }}
              />
              <div className="more-tab-content">
                {/* General (tmpl:491-550) */}
                <div className={subPane(bypassSubTab === 'general')} id="general_more">
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-6 input-part-left">
                      <div className="row">
                        <div className="col-xs-9 first_column_input">
                          <SubTitle>General</SubTitle>
                          <DocSelect name="bypass_type_id" label="Type" />
                          <DocSelect name="bypass_location_id" label="Location" />
                          {/* ★ legacy shows Width for BOTH manufacturers — tmpl:533-544
                              carries no `hide-for-heatex`. Heatex only drops it from the
                              RATE payload (js:2661-2663), which buildRateInput already
                              does. The port hid the field itself. */}
                          <Num name="bypass_width" label="Width" unit="in" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bypass Damper (tmpl:551-677) — ★ RESTORED group. */}
                <div className={subPane(bypassSubTab === 'bypass')} id="bypass_more">
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-6 input-part-left">
                      <div className="row">
                        <div className="col-xs-9 first_column_input">
                          <SubTitle>Bypass Damper</SubTitle>
                          <DocSelect name="bypass_side_id" label="Side" rowClass="bypass_type_open" />
                          <DocSelect name="bypass_dmr_config_id" label="Configuration" rowClass="bypass_type_open" />
                          <DocSelect name="bypass_dmr_location_id" label="Damper Location" rowClass="bypass_type_open" />
                          <DocSelect name="bypass_dmr_drive_location_id" label="Drive Location" rowClass="bypass_type_open" />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-12 col-lg-6 input-part-right">
                      {/* the two `row empty` spacers are legacy's (tmpl:628, 656) — they
                          are what line Material/Finish up with Side/Configuration
                          opposite, and `.empty {min-height: 29.5px}` (common.css:20921)
                          is the rule that gives them height. */}
                      <div className="row empty" />
                      <MasterSelect name="bypass_dmr_mtl_id" label="Material" rowClass="bypass_type_open" />
                      <MasterSelect name="bypass_dmr_fin_id" label="Finish" rowClass="bypass_type_open" />
                      <div className="row empty" />
                      <DocSelect name="dmrs_supplied_by_id" label="Dampers Supplied By" rowClass="bypass_type_open" />
                    </div>
                  </div>
                </div>
                {/* Recirculation (tmpl:678-726) — ★ RESTORED group. */}
                <div className={subPane(bypassSubTab === 'recirculation')} id="recirculation_more">
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-6 input-part-left">
                      <div className="row">
                        <div className="col-xs-9 first_column_input">
                          <SubTitle>Recirculation</SubTitle>
                          <DocSelect name="recirc_dmr_config_id" label="Configuration" />
                          <DocSelect name="recirc_dmr_location_id" label="Location" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ----- Settings (tmpl:1068-1159) — ★ RESTORED tab ----- */}
          <div className={tabClass('Settings')} id={`${idPrefix}Settings`}>
            <div className="col-xs-12 col-md-12 col-lg-6 input-part-left">
              <div className="row">
                <div className="col-xs-9 first_column_input">
                  {/* ★ RESTORED: calc_mode (drives bank geometry) + max_allowed_dp. */}
                  <OptionSelect name="calc_mode_id" label="Calculation Mode" selectClass="model_field"
                    options={(masterLists.calc_mode_id ?? []) as OptionRow[]} />
                  <DocSelect name="type_desired_efficiency_id" label="Desired Efficiency Type" />
                  {/* ★ the one asterisk this dialog draws. checkAndUpdateRequiredFields
                      (js:2691-2713) marks a label only when the row's control is NOT a
                      `<select>`, and `min_desired_efficiency` is the single rendered
                      text input in REQUIRED_FIELDS (js:99-174). */}
                  <Num name="min_desired_efficiency" label="Minimum Efficiency" unit="%" required />
                  <SelectRow label="Max Allowed PD" dataName="max_allowed_dp" unit="in WC">
                    <PhxNumberInput name="max_allowed_dp" value={s.max_allowed_dp} formatter="decimal"
                      autoComplete="off" className="model_field padding-left-5px"
                      dataMax={maxAllowedApd ?? 16} dataMin={0}
                      onCommit={(raw) => onModelField('max_allowed_dp', raw)} />
                  </SelectRow>
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-md-12 col-lg-6 input-part-right">
              {/* ★ DOWNGRADE FIX: certification multiCheckbox (was a single select).
                  ★ The GROUP is unconditional (tmpl:1130-1155) — the button and the
                  "Certification" label render in header mode too. What legacy gates on a
                  saved id is only the OPTIONS fetch,
                  `/plate_heat_exchangers/<id>/certifications/` (js:939), so header mode
                  shows the control with an empty list. The port had gated the whole
                  thing on `!headerMode && modelId` and lost the row. */}
              <ul className="certifications-ul">{<CertificationEditor />}</ul>
            </div>
          </div>

          {/* ----- Pricing (tmpl:1160-1186 + pricing_method_inputs.tmpl) ----- */}
          <div className={tabClass('Pricing', 'col-xs-12')} id={`${idPrefix}Pricing`}>
            <div className="col-xs-12 col-md-12 col-lg-6 float-left">
              <div className="row">
                <div className="col-xs-9 first_column_input">
                  <SelectRow label="Method" dataName="pricing_method_id" cellClass="col-xs-12 right text-right">
                    <select name="pricing_method_id" data-name="pricing_method_id" className="pricing_method_field"
                      value={s.pricing_method_id == null ? '' : String(s.pricing_method_id)}
                      onChange={(e) => onModelField('pricing_method_id', e.target.value)}>
                      {/* ★ no blank option and no hardcoded fallback: dev renders
                          `{{#each pricing_methods}}` and nothing else (tmpl:1172-1177),
                          so "no method" is not a choice there. The port's List/User pair
                          was masking a 404 — see PHX_PRICING_METHODS_URL. */}
                      {pricingMethods.map((r) => (
                        <option key={String(r.id)} value={String(r.id)} data-value={String((r as any).value ?? r.id)}>
                          {optionLabel(r, 'name')}
                        </option>
                      ))}
                    </select>
                  </SelectRow>
                  {/* pricing inputs shown for method != 1 && != null (legacy js:1608).
                      method 2 → user COST; else (List) → ★ RESTORED price_* fields.
                      dev renders the pair as ONE row — label "Heat-Ex | Freight" and
                      two `col-xs-6` inputs in a `col-xs-6 input-part four-input-row`
                      (pricing_method_inputs.tmpl) — not as two stacked rows. */}
                  {showPricingInputs && (
                    <div className="row pricingInputs pricing_method_input">
                      <PricingPair cost={pmId === 2} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* ===== JOB B — OUTPUT block: a SIBLING of #plate_heat_exchanger (tmpl:1190).
          No `col-xs-12`: the tmpl has none, and adding one insets the whole Output
          pane 15px per side so its rails stop lining up with the Input pane's.
          (Same defect the heat wheel had, PR #1155.) ===== */}
      <div className={`plate_heat_exchanger output-part rated-item output-group output-content${ratedRows?.length ? ' rating' : ''}`}>
        {/* ★ The output body is gated on a rate having happened. Legacy wraps the
            whole thing in `{{#ifCompare ok 'or' rated_phx}}` (rated.outputs.tmpl:28)
            and only calls render_outputs after a rate, so before you press RATE the
            area under the grey input pane is EMPTY. The port rendered the table
            unconditionally, which drew the column header row ("Model | Wet Eff Wi %
            | …") and the Mfg's Notes block on a never-rated dialog. */}
        {hasRated && (
          <PlateHeatExchangerRatedOutputs
            rows={rows}
            ratedIndex={ratedIndex}
            // legacy `changePhx` (js:3194-3199) — re-render on the new index,
            // keeping whichever output tab was open.
            onSelectRow={(i) => {
              setOutputTab(effectiveOutputTab);
              setRatedIndex(i);
            }}
            fromApi={fromApi.current}
            ratedPhx={ratedPhx}
            currentModel={(detail as Record<string, unknown> | undefined) ?? {}}
            state={s}
            phxData={phxCatalog ?? masterPhx}
            dataForRate={outputDataForRate}
            bankDeep={s.bank_deep}
            airStreams={streams}
            shaftShapes={(shaftShapesQ.data as OptionRow[] | undefined) ?? []}
            shaftDiameters={(shaftDiametersQ.data as OptionRow[] | undefined) ?? []}
            shaftLengths={(shaftLengthsQ.data as OptionRow[] | undefined) ?? []}
            notes={mfgNotes}
            errors={rateErrors}
            warnings={rateWarnings}
            splitedNotes={splitedNotes}
            notFound={notFound}
            ratedDatetime={ratedDatetime.current}
            version={rateVersion}
            isHeatex={isHeatex}
            // the season sub-tab is SHARED with the input pane: legacy binds
            // `click .summer-winter-tabing li` document-wide and both panes carry
            // that class, then `syncInputPerformanceTab` (js:3117-3130) mirrors it
            // back. One piece of state expresses both.
            season={season}
            onSeasonChange={setSeason}
            outputTab={effectiveOutputTab}
            onOutputTabChange={setOutputTab}
          />
        )}
        {/* ★ REMOVED: a second `.certification_output` echo. The legacy
            `.output-part` holds the rated-outputs template and nothing else —
            the certification read-out belongs to the INPUT pane's Settings tab
            (selector.tmpl:1151, the `outputElementSelector: ".certification_output"`
            the multiCheckbox writes into, js:948), and that one is rendered
            above. The duplicate drew the cert list a second time between the
            Mfg's Notes block and the "Rated on" line, which dev does not. */}
        {isRating && <div className="getting-preloader preloader dotted-preloader active" />}
      </div>
    </div>
    </PhxCtx.Provider>
  );
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** legacy MFG_DEFAULT_SERIES (js:969-972). */
const PHX_MFG_DEFAULT_SERIES: Record<number, number> = { 167: 916, 52: 276 };

/**
 * The per-season ENTERING CONDITIONS legacy's `models_field` sources from the
 * FORM — `dataForSave.modelField.<k> ? … : current_model.<k>`
 * (plate_heat_exchanger.selector.js:1953-2101) — and NOT from the rated
 * candidate. They are the rate's *input*, so the vendor never echoes them back.
 *
 * The port ran the whole `{oa,sa,ra,ea} × {air_stream, airflow_actual, db_temp,
 * wb_temp, rh, apd, …}` matrix off the rated row `r`, guarded by
 * `if (r[key] !== undefined)`. For every one of these keys `r[key]` IS
 * undefined, so Select wrote nothing and the columns kept whatever was already
 * saved — NULL on a unit first rated in React. Measured on dev3: the only three
 * `ksdm_ahu_component_phx` rows with a NULL `oa_airflow_actual_winter` are the
 * three highest ids, i.e. the React-era ones; all 15 rows the Backbone app
 * selected carry it.
 *
 * That NULL is what the reported bug is: the output sheet draws a season pane
 * only when its airflow is `> 0`
 * (`plate_heat_exchanger.rated.outputs.tmpl:415` winter / `:806` summer, over
 * `dataForRate`, which legacy re-seeds from the rendered input DOM on every open
 * — js:1622-1641). Rate + Select + reopen therefore drew an EMPTY Performance
 * tab, because the airflow the gate reads had never been persisted.
 *
 * Only OA and RA have controls in this dialog; the SA/EA members have none and
 * are plain saved-value passthroughs — the same shape as `casing_width` /
 * `bypass_width` in PR #1196, in the opposite direction.
 */
const PHX_SELECT_FORM_SEASON_FIELDS: readonly string[] = [
  // Air Stream selects (js:1966-1973) — all four streams, both seasons.
  'oa_air_stream_summer_id', 'sa_air_stream_summer_id',
  'ra_air_stream_summer_id', 'ea_air_stream_summer_id',
  'oa_air_stream_winter_id', 'sa_air_stream_winter_id',
  'ra_air_stream_winter_id', 'ea_air_stream_winter_id',
  // Airflow (js:2011 sa_summer / :2013 ea_summer / :2020 sa_winter /
  // :2039 oa_winter / :2051 ra_winter / :2056 ea_winter). The summer OA/RA pair
  // is added by the conditional block below, not here.
  'sa_airflow_actual_summer', 'ea_airflow_actual_summer',
  'sa_airflow_actual_winter', 'oa_airflow_actual_winter',
  'ra_airflow_actual_winter', 'ea_airflow_actual_winter',
  // DB temperature — OA/RA only (js:1984 oa_winter, :2052 ra_winter; the summer
  // pair comes from the conditional block below). Every SA/EA member is rated.
  'oa_db_temp_winter', 'ra_db_temp_winter',
  // WB temperature (js:2001/:2003 summer, :2024/:2026 winter) — OA/RA only;
  // the SA/EA pair is rated output.
  'oa_wb_temp_summer', 'ra_wb_temp_summer',
  'oa_wb_temp_winter', 'ra_wb_temp_winter',
  // RH (js:2035 oa_winter / :2053 ra_winter). The summer OA/RA pair is in the
  // conditional block; every SA/EA member is rated.
  'oa_rh_winter', 'ra_rh_winter',
];

/**
 * The SA/EA summer members legacy takes RATED-FIRST with a saved fallback —
 * `rated_phxs[idx].<k> ? rated_phxs[idx].<k> : current_model.<k>`
 * (js:2003 / :2010 / :2016 / :2018 / :2021). The port had only the rated arm
 * (`if (r[key] !== undefined)`), so a vendor that omits one left the key out of
 * the payload entirely instead of writing the saved value back. Their winter
 * counterparts are unconditionally rated on dev and stay in the loop above.
 */
const PHX_SELECT_RATED_THEN_SAVED_FIELDS: readonly string[] = [
  'sa_db_temp_summer', 'ea_db_temp_summer', 'ea_rh_summer',
  'sa_apd_summer', 'ea_apd_summer',
];

/**
 * The six summer fields legacy appends CONDITIONALLY after the map is built
 * (js:2147-2164), from the form exactly as above. They are written when the user
 * staged a summer OA airflow, or when the saved row already carries one;
 * otherwise they are left alone so the summer-nulling pass below can blank the
 * season.
 */
const PHX_SELECT_FORM_SUMMER_FIELDS: readonly string[] = [
  'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
  'oa_db_temp_summer', 'ra_db_temp_summer',
  'oa_rh_summer', 'ra_rh_summer',
];

/**
 * legacy `self.setNullInModel` (js:1263-1269): set when the user CLEARS either
 * summer airflow, and the only thing it does is suppress the block above so the
 * season really is blanked.
 */
function phxSummerCleared(staged: Record<string, unknown>): boolean {
  return (['oa_airflow_actual_summer', 'ra_airflow_actual_summer'] as const).some(
    (k) => k in staged && !staged[k]
  );
}

/** the phx CMS `manufacturer` collection + dev's pick (js:973). */
function resolvePhxMfg(doc: unknown): {
  list: Array<Record<string, unknown>>;
  defaultMfgId: number | undefined;
} {
  const list = (((doc as Record<string, any> | undefined)?.plate_heat_exchanger
    ?.manufacturer) ?? []) as Array<Record<string, unknown>>;
  const pick = list.find((e) => Number(e.id) === 52) ?? list[0];
  return { list, defaultMfgId: pick?.id as number | undefined };
}

// the doc-resolved field set (used by buildRateInput to resolve names).
const PHX_CMS_PATHS_LOCAL: Record<string, string> = {
  flow_type_id: '', type_desired_efficiency_id: '', plate_spacing_code_id: '',
  face_one_definition_id: '', face_two_definition_id: '', bypass_location_id: '',
  bypass_type_id: '', bypass_side_id: '', bypass_dmr_config_id: '',
  bypass_dmr_location_id: '', bypass_dmr_drive_location_id: '',
  dmrs_supplied_by_id: '', recirc_dmr_config_id: '', recirc_dmr_location_id: '',
};

/** bank wide×high calc-mode logic (legacy setBankWideByHigh, 1594-1641). Mutates
 * the passed state object. mode 1 = single (blank, deep 1); 2 = manual wide×high
 * (deep 1); 3 = locked 1×1, deep 2. */
function applyCalcMode(s: Record<string, unknown>, calcModeId: number | null): void {
  if (calcModeId == 1) {
    s.bank_wide = null;
    s.bank_high = null;
    s.bank_deep = 1;
  } else if (calcModeId == 3) {
    s.bank_wide = 1;
    s.bank_high = 1;
    s.bank_deep = 2;
  } else if (calcModeId == 2) {
    s.bank_deep = 1;
  }
}

/**
 * Build the selectPhx WRITE payload (§33.4). Pure so the verifier can read the
 * exact write shapes:
 *   - models_field → current_model.set (auto-saves PATCH ahu/plate_heat_exchangers/<id>)
 *   - rating_fields → self.model.set (the rated_phx_model — PATCH rating_mode/<id>)
 *   - params_fields → catalogueFields.master_phx_id.resetFilter (catalogue filter)
 */
export function buildSelectPayload(
  s: Record<string, any>,
  r: Record<string, any>,
  /** legacy `PhxMasterCollection.toJSON()[0]` — the picked candidate's catalogue
   *  row. Every `plate_spacing` / `plate_thickness` / `casing_depth` / `shape_id`
   *  below is `catalogue || rated` on dev (js:2120-2143), and the vendor row
   *  carries none of the four for Recuperator — so without it Select wrote four
   *  nulls onto the component. */
  cat: Record<string, any> = {},
  /** legacy `self.dataForSave.modelField` — ONLY the fields the user actually
   *  edited in this dialog session, not the whole seeded state. `bypass_width`
   *  is the one models_field entry legacy keys on it (js:1944). */
  staged: Record<string, any> = {},
  /** legacy `self.current_model.toJSON()` — the SAVED component row, i.e. the
   *  `:` arm of every `dataForSave.modelField.<k> ? … : current_model.<k>`.
   *  Distinct from `s`, which already has the staged edits merged in: legacy
   *  falls back to the saved value when an edit is falsy, so clearing a field
   *  restores what was there rather than writing the blank. */
  saved: Record<string, any> = {}
): {
  modelsField: Record<string, unknown>;
  ratingFields: Record<string, unknown>;
  paramsFields: Record<string, unknown>;
} {
  const mfg = Number(s.mfg_id);
  const fb = (k: string) => (s[k] != null ? s[k] : r[k]); // staged-or-rated.

  const modelsField: Record<string, unknown> = {
    // config / geometry ids (corner profile / bypass / dmrs / plate_pattern).
    corner_profile_style_id: fb('corner_profile_style_id'),
    corner_profile_mtl_id: fb('corner_profile_mtl_id'),
    corner_profile_finish_id: fb('corner_profile_finish_id'),
    bypass_location_id: fb('bypass_location_id'),
    bypass_type_id: fb('bypass_type_id'),
    bypass_side_id: fb('bypass_side_id'),
    // ★ staged-or-RATED, not staged-or-saved (js:1944:
    //   `dataForSave.modelField.bypass_width ? … : rated_phxs[idx].bypass_width`).
    // The vendor returns the bypass width with the candidate; the dialog has no
    // control that seeds it, so `s.bypass_width` is null on every unit that has
    // never been selected in the Backbone app — and Select wrote that null back.
    // `selection_validations` (models_component_plate_exchanger.py:1443) fails a
    // Heat-Exchanger-w/Bypass component with no bypass_width, which is how a
    // just-selected PHX still read "Rating & Selection Incomplete!".
    bypass_width: staged.bypass_width ? staged.bypass_width : r.bypass_width,
    bypass_dmr_config_id: fb('bypass_dmr_config_id'),
    bypass_dmr_location_id: fb('bypass_dmr_location_id'),
    bypass_dmr_drive_location_id: fb('bypass_dmr_drive_location_id'),
    bypass_dmr_mtl_id: fb('bypass_dmr_mtl_id'),
    bypass_dmr_fin_id: fb('bypass_dmr_fin_id'),
    dmrs_supplied_by_id: fb('dmrs_supplied_by_id'),
    recirc_dmr_config_id: fb('recirc_dmr_config_id'),
    recirc_dmr_location_id: fb('recirc_dmr_location_id'),
    plate_pattern_id: fb('plate_pattern_id'),
    plate_orientation_id: fb('plate_orientation_id'),
    plate_mtl_id: fb('plate_mtl_id'),
    plate_finish_id: fb('plate_finish_id'),
    end_plate_style_id: fb('end_plate_style_id'),
    end_plate_mtl_id: fb('end_plate_mtl_id'),
    end_plate_finish_id: fb('end_plate_finish_id'),
    temp_rating_id: fb('temp_rating_id'),
    seal_id: fb('seal_id'),
    mounting_id: fb('mounting_id'),
    configuration_id: fb('configuration_id'),
    // ★ certification is a multiCheckbox RELATION (/certifications/), NOT a model
    // field — legacy's models_field has no certification_id (js:2060-2135). The
    // downgrade port had written a single certification_id here; removed. The
    // selected set commits as a create/delete diff in commitPhxChain.
    flow_type_id: fb('flow_type_id'),
    face_one_definition_id: fb('face_one_definition_id'),
    face_two_definition_id: fb('face_two_definition_id'),
    type_desired_efficiency_id: fb('type_desired_efficiency_id'),
    min_desired_efficiency: s.min_desired_efficiency,
    // ★ JOB A restored: max_allowed_dp is a Settings model field (legacy
    // models_field, js line 58 in the select apply). Was dropped with the tab.
    max_allowed_dp: s.max_allowed_dp,
    calc_mode_id: s.calc_mode_id,
    // shaft ids (from the .shaft_* datasets at select).
    dmr_shaft_shape_id: s.dmr_shaft_shape_id,
    dmr_shaft_dia_id: s.dmr_shaft_dia_id,
    dmr_shaft_length_id: s.dmr_shaft_length_id,
    // bank / casing.
    bank_wide: s.bank_wide, bank_high: s.bank_high, bank_deep: s.bank_deep,
    // ★ the RATED row's casing width, unconditionally (js:1976) — this is the
    // component's Overall W, and dev's template has no casing_width control at
    // all, so it can only ever come from the rate. Reading it off `s` left the
    // column NULL (blank Overall W, and `not self.casing_width` in
    // selection_validations → "Rating & Selection Incomplete!").
    casing_width: r.casing_width,
    height_from_floor: s.height_from_floor,
    // ★ module_configuration_id persists to the model too (legacy models_field +
    // the delete-if-falsy below). Was referenced by the delete but never set.
    module_configuration_id: s.module_configuration_id,
    pricing_method_id: s.pricing_method_id,
    cost_product_user: s.cost_product_user,
    cost_freight_user: s.cost_freight_user,
    // List-method pricing fields (pricing_method_inputs.tmpl:14/21 price_* branch).
    price_product_user: s.price_product_user,
    price_freight_user: s.price_freight_user,
    master_phx_id: r.id || r.master_phx_id || undefined,
    generic_mfg: s.__generic_mfg ?? false,
    rating_datetime: s.__rated_datetime ?? null,
    // ---- staged-or-model form fields the port dropped ----
    // All four are rendered and editable in this dialog and all four are real
    // writable columns. Legacy sends each as
    // `dataForSave.modelField.<k> ? … : current_model.<k>` — which is what
    // `fb()` already does (plate_heat_exchanger.selector.js:1981 oa_sp,
    // :1989 select_mode_id, :2065 blank_off_mtl_gauge_id, :2079
    // dist_from_left). Without them, editing "SP Outside" (or any of the
    // other three) and pressing Select silently discarded the edit.
    oa_sp: fb('oa_sp'),
    select_mode_id: fb('select_mode_id'),
    blank_off_mtl_gauge_id: fb('blank_off_mtl_gauge_id'),
    dist_from_left: fb('dist_from_left'),
  };

  // ---- rated-row outputs that fall outside the seasonal loops below ----
  // Legacy sends each as a plain `self.rated_phxs[idx].<field>`
  // (plate_heat_exchanger.selector.js:1961 / :1983 / :2048 / :2066 / :2069 /
  // :2071-2072 / :2106-2108). They carry no `{oa,sa,ra,ea}` prefix and — bar
  // `sma_db_temp_winter` — no season suffix, so the `{prefix} × {season}` loops
  // that carried the rest of the rated output across never reached them. That
  // is exactly how they came to be dropped.
  //
  // `energy_efficiency_en13053` and `hr_class` are the sharpest two: the port
  // already NULLS both on the rated row for display (PHX_FIELDS_TO_NULLIFY) and
  // then never wrote them back, so a Heatex re-rate left the PREVIOUS vendor's
  // value sitting in the column — the precise staleness that nullify list
  // exists to prevent.
  for (const k of [
    'airway_width', 'bypass_apd', 'bypass_velocity', 'bypass_airflow',
    'number_of_modules', 'product_code', 'e_plate_style',
    'energy_efficiency_en13053', 'hr_class', 'sma_db_temp_winter',
    'blank_off_selection_id',
  ]) {
    if (r[k] !== undefined) modelsField[k] = r[k];
  }

  // all seasonal perf outputs (from the rated row).
  for (const sea of ['summer', 'winter'] as const) {
    for (const pre of ['oa', 'sa', 'ra', 'ea'] as const) {
      for (const f of [
        'air_stream', 'airflow_actual', 'db_temp', 'wb_temp', 'rh',
        'face_velocity', 'apd', 'condensation_rate', 'frost_surface_percent',
      ]) {
        const key = f === 'air_stream' ? `${pre}_air_stream_${sea}_id` : `${pre}_${f}_${sea}`;
        if (r[key] !== undefined) modelsField[key] = r[key];
      }
    }
    for (const f of [
      'wet_temp_effectiveness', 'dry_temp_effectiveness', 'wet_temp_ratio',
      'dry_temp_ratio', 'frost_starting_temp', 'hrs_capacity', 'erp_1253_2014',
      // legacy :2099-2100 — sent for BOTH seasons, and dropped here with the
      // rest of the season-suffixed rated output above.
      'dry_temp_eff_eaequalssa',
    ]) {
      const key = `${f}_${sea}`;
      if (r[key] !== undefined) modelsField[key] = r[key];
    }
    const ash = `efficiency_${sea}_ashrae`;
    if (r[ash] !== undefined) modelsField[ash] = r[ash];
  }

  // ★ the entering conditions, back off the FORM where legacy reads them. This
  // runs AFTER the rated-row loop above so it wins the keys the two share, which
  // is the order legacy writes them in, and BEFORE the season nulling below,
  // which reads `oa_airflow_actual_{winter,summer}` straight back out of the map
  // (js:2178-2189). See PHX_SELECT_FORM_SEASON_FIELDS for why this is the whole
  // of the "reopen shows an empty Performance tab" bug.
  const formField = (k: string) => (staged[k] ? staged[k] : saved[k]);
  for (const k of PHX_SELECT_FORM_SEASON_FIELDS) modelsField[k] = formField(k);
  for (const k of PHX_SELECT_RATED_THEN_SAVED_FIELDS) modelsField[k] = r[k] ? r[k] : saved[k];
  // the summer six, on legacy's condition (js:2147-2164).
  if (!phxSummerCleared(staged)) {
    if (staged.oa_airflow_actual_summer || saved.oa_airflow_actual_summer != null) {
      for (const k of PHX_SELECT_FORM_SUMMER_FIELDS) modelsField[k] = formField(k);
    }
  }

  // missing-season nulling (legacy 2096-2105): blank season ⇒ null its fields.
  if (!modelsField.oa_airflow_actual_winter) {
    (WINTER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
      if (k !== 'oa_air_stream_winter_id' && k !== 'ra_air_stream_winter_id') modelsField[k] = null;
    });
  }
  if (!modelsField.oa_airflow_actual_summer) {
    (SUMMER_REQUIRED_FIELDS_FOR_RATE[mfg] ?? []).forEach((k) => {
      if (k !== 'oa_air_stream_summer_id' && k !== 'ra_air_stream_summer_id') modelsField[k] = null;
    });
  }
  // module_configuration_id deleted when falsy (legacy 2108-2110).
  if (!s.module_configuration_id) delete modelsField.module_configuration_id;

  // rating_fields → the rated_phx_model (legacy 2027-2050).
  // `rated-then-saved` for the two catalogue keys: the vendor answers with the
  // size / spacing code it actually picked and legacy stores THAT
  // (`rated[k] ? rated[k] : current_model[k]`, js:2113-2114) — the port was
  // storing the unset search-field state instead, so a Select wrote a null
  // size_id over a real pick.
  const catFirst = (k: string) => cat[k] ?? r[k];
  const ratingFields: Record<string, unknown> = {
    mfg_id: s.mfg_id,
    model: r.model,
    plate_spacing_code_id: r.plate_spacing_code_id ?? s.plate_spacing_code_id,
    size_id: r.size_id ?? s.size_id,
    plate_pattern_id: fb('plate_pattern_id'),
    max_allowed_dp: r.max_allowed_dp,
    flow_type_id: fb('flow_type_id'),
    pricing_method_id: s.pricing_method_id,
    casing_depth: catFirst('casing_depth') ?? s.casing_depth,
    casing_height: r.casing_height ?? s.casing_height,
    operating_weight: r.operating_weight,
    shipping_weight: r.shipping_weight,
    plate_spacing: catFirst('plate_spacing') ?? s.plate_spacing,
    plate_thickness: catFirst('plate_thickness'),
    series_id: s.series_id,
    temp_rating_id: fb('temp_rating_id'),
    seal_id: fb('seal_id'),
    configuration_id: fb('configuration_id'),
    shape: r.shape,
    shape_id: catFirst('shape_id') ?? s.shape_id,
    module_qty: s.module_qty,
  };

  // params_fields → the catalogue filter (legacy 2051-2062).
  const paramsFields: Record<string, unknown> = {
    mfg_id: s.mfg_id,
    series_id: s.series_id,
    plate_spacing_code_id: r.plate_spacing_code_id ?? s.plate_spacing_code_id,
    size_id: r.size_id ?? s.size_id,
    shape_id: catFirst('shape_id') ?? s.shape_id,
    plate_spacing: catFirst('plate_spacing') ?? s.plate_spacing,
    plate_thickness: catFirst('plate_thickness'),
    flow_type_id: fb('flow_type_id'),
    casing_depth: catFirst('casing_depth') ?? s.casing_depth,
    casing_height: r.casing_height ?? s.casing_height,
  };

  return { modelsField, ratingFields, paramsFields };
}

// ---------------------------------------------------------------------------
// The legacy input row (plate_heat_exchanger.selector.tmpl, e.g. :53-72).
//
//   <div class="row">
//     <div class="col-xs-6 label-part"><span data-name=…>Label</span>
//                                      <span class="right uom">in</span></div>
//     <div class="col-xs-6 input-part">
//       <div class="row"><div class="col-xs-12">  ← control lives HERE
//
// Three things the port had dropped, all of them load-bearing:
//
//  * the `col-xs-6 / col-xs-6` split — the port used `col-xs-5 / col-xs-7`, so
//    every label was 8.3% narrow and every control 8.3% wide of dev's.
//  * the inner `.row > .col-xs-12` around the control. `common.css:20976-20989`
//    sizes the left-hand selects through it —
//    `.component-rating-mode #properties_more .first_column_input .input-part
//     .col-xs-12 select { width: calc(151% - 5px) !important }` (and 102% on
//    #internal_plates_more) — so without that div every left-column select fell
//    back to the generic `.component-rating-mode select { width: calc(100% - 5px) }`
//    and rendered a third too narrow.
//  * the unit span's class. Legacy is `right uom` (`:20936` — 11px, #a5a5a5);
//    the port emitted `percent right`, which is a DIFFERENT rule (`:20939`) —
//    a 25%-wide left-floated box with `margin-top: 9px`. That is why the units
//    sat away from their labels.
//
// `labelCols` is per-pane, not global: properties/end-plates/corner-profiles
// are 6/6, internal-plates is 4/8 (tmpl:307), performance is 4/8 (tmpl:761).
// ---------------------------------------------------------------------------
const SelectRow = ({ label, dataName, unit, unitTemp, labelCols = 6, rowClass, cellClass, required, children }: {
  label: React.ReactNode; dataName?: string; unit?: string; unitTemp?: boolean;
  labelCols?: number; rowClass?: string; cellClass?: string; required?: boolean;
  children: React.ReactNode;
}) => (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className={`col-xs-${labelCols} label-part`}>
      {/* `.required` draws the asterisk via `::after` (common.css:29083). Legacy
          adds it to the label SPAN in checkAndUpdateRequiredFields (js:2727-2732). */}
      {dataName ? <span data-name={dataName} className={required ? 'required' : undefined}>{label}</span> : label}
      {unit && <span className={unitTemp ? 'right temp uom' : 'right uom'}>{unit}</span>}
    </div>
    <div className={`col-xs-${12 - labelCols} input-part`}>
      <div className="row">
        {/* `cellClass` is only ever the pricing Method row's `col-xs-12 right
            text-right` (tmpl:1170); everything else is a plain `col-xs-12`. */}
        <div className={cellClass ?? 'col-xs-12'}>{children}</div>
      </div>
    </div>
  </div>
);

/**
 * The pane sub-heading (tmpl:299-303, 375-379, 425-429, 494-498). Legacy puts it in
 * a full `.row > .col-xs-6.label-part`; the port had collapsed it to
 * `<div class="row sub-titles">` with the span as a direct child, so it lost the
 * column's 15px padding and sat flush against the pane edge.
 */
const SubTitle = ({ children, labelCols = 6 }: { children: React.ReactNode; labelCols?: number }) => (
  <div className="row">
    <div className={`col-xs-${labelCols} label-part sub-titles`}>
      <span data-name="" className="text-uppercase sub-titles__text">{children}</span>
    </div>
  </div>
);

/**
 * A row whose input-part is NOT the plain `.row > .col-xs-12` single cell —
 * series+shape (tmpl:79-89), bank wide×high (tmpl:228-241), face definition 1/2
 * (tmpl:274-291) and the pricing pair (pricing_method_inputs.tmpl:9-22) each
 * split the input-part into their own columns. Same outer shape, caller-supplied
 * inner cells.
 */
const SplitRow = ({ label, dataName, unit, labelCols = 6, rowClass, children }: {
  label: React.ReactNode; dataName?: string; unit?: string;
  labelCols?: number; rowClass?: string; children: React.ReactNode;
}) => (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className={`col-xs-${labelCols} label-part`}>
      {dataName ? <span data-name={dataName}>{label}</span> : label}
      {unit && <span className="right uom">{unit}</span>}
    </div>
    <div className={`col-xs-${12 - labelCols} input-part`}>
      <div className="row">{children}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// sub-views — MODULE SCOPE ON PURPOSE (see the PhxViewCtx note above)
// ---------------------------------------------------------------------------

/**
 * ★ PERFORMANCE-PANE TAB ORDER — Tab walks DOWN a column, not ACROSS the pair.
 *
 * The pane is laid out one property per row with SUPPLY on the left and EXHAUST
 * on the right, so document order alternates supply/exhaust/supply/exhaust and
 * plain sequential tabbing hops sideways. Legacy overrides that with an explicit
 * positive tabindex on every control in the pane: SUPPLY (`oa_*`) is `12` and
 * EXHAUST (`ra_*`) is `17` (plate_heat_exchanger.selector.tmpl:767-888 winter,
 * :933-1043 summer — Air Stream, Airflow, DB, RH/WB and Pressure In alike).
 *
 * Equal positive tabindex values are visited in document order among themselves
 * and lower values come first, so the browser walks the whole supply column
 * top-to-bottom, then the whole exhaust column. The read-only Altitude box keeps
 * legacy's `tabindex="-1"` (:902 / :1056) and stays out of the sequence.
 *
 * The values are legacy's own, kept verbatim rather than renumbered: they are
 * POSITIVE, so they also pull these controls ahead of every other (natural
 * order) field in the dialog. That is legacy's behaviour, quirk included.
 */
const PERF_TAB_INDEX = { supply: 12, exhaust: 17 } as const;
const side = (name: string): 'supply' | 'exhaust' =>
  (name.startsWith('oa_') ? 'supply' : 'exhaust');

/**
 * ★ THE SECOND HALF OF THE UNTYPABLE-NUMBER BUG — legacy never reformats a
 * number box while you are in it.
 *
 * `NumericInputFormatter.bindEvents` (universal_fn/formatter.js:187-192) leaves
 * `oninput` COMMENTED OUT — the comment above it is literally "Don't doing
 * anything while editing" — and hangs the decode/round/re-encode off `onchange`,
 * i.e. blur. The view commits on blur too (`"change .model_field":
 * "changeModelField"`, plate_heat_exchanger.selector.js events). So on dev you
 * type `72.5` into DB Temperature freely and it becomes `72.50` when you leave.
 *
 * React ran the formatter on EVERY keystroke instead. `7` was rewritten to
 * `7.00` under the caret, the next character landed after it (`7.002`), the
 * formatter snapped that back to `7.00`, and the box was stuck on the first
 * digit forever. The integer boxes escaped only because their format is a no-op
 * below 1,000.
 *
 * Fix: show the formatted text only while the box is NOT being edited. On focus
 * the current text becomes a local draft, the draft wins for as long as the
 * field has focus, and blur drops it so the formatted model value comes back.
 * COMMIT TIMING IS UNCHANGED — still every input event; only the DISPLAY is
 * deferred, so nothing downstream of `onModelField` moves.
 *
 * Same shape as `selectorNumberInput.tsx`'s draft, deliberately not that
 * component: it formats through `formatLegacyNumber`, which drops trailing zeros
 * once a separator lands (`1200` → `1,200`, not `1,200.00`), and this dialog's
 * resting text is `formatDecimalDisplay`/`formatIntegerDisplay`. Reusing it
 * would have quietly changed every rested value in the pane.
 */
const PhxNumberInput = ({
  name, dataName, value, formatter, className, readOnly, autoComplete,
  dataMin, dataMax, dataStep, tabIndex, onCommit,
}: {
  name: string; dataName?: string; value: unknown; formatter: 'integer' | 'decimal';
  className?: string; readOnly?: boolean; autoComplete?: string;
  dataMin?: number; dataMax?: number; dataStep?: number; tabIndex?: number;
  onCommit?: (raw: string) => void;
}) => {
  /** non-null only while this field has focus. */
  const [draft, setDraft] = useState<string | null>(null);
  const display = formatter === 'integer' ? formatIntegerDisplay(value) : formatDecimalDisplay(value);
  return (
    <input type="text" name={name} data-name={dataName ?? name} data-formatter_type={formatter}
      autoComplete={autoComplete} className={className}
      readOnly={readOnly} tabIndex={readOnly ? -1 : tabIndex}
      data-min={dataMin} data-max={dataMax} data-step={dataStep}
      value={draft ?? display}
      onFocus={(e) => setDraft(e.target.value)}
      onChange={(e) => {
        setDraft(e.target.value);
        if (!readOnly) onCommit?.(e.target.value);
      }}
      onBlur={() => setDraft(null)} />
  );
};

/**
 * The bare CMS-doc <select>, without a row around it — used by the cells that
 * legacy lays out itself (face definition 1/2, tmpl:275-291).
 */
const RawDocSelect = ({ name }: { name: string }) => {
  const { s, phxDoc, onSearchField } = usePhx();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = phxCmsOptionList(phxDoc as PhxCmsDoc, name);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  const selLabel = valStr ? phxCmsSelectedLabel(phxDoc as PhxCmsDoc, name, value as any) : '';
  return (
    <select name={name} data-name={name} className="search_field" value={valStr}
      onChange={(e) => onSearchField(name, e.target.value)}>
      {valStr === '' && <option value="" />}
      {rows.map((r) => (
        <option key={String(r.id)} value={String(r.id)} data-value={String(r.id)}>
          {String(r.label ?? '')}
        </option>
      ))}
      {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
    </select>
  );
};
const FaceSelect = RawDocSelect;

// ----- doc select (flow/spacing/face/bypass/recirc) -----
const DocSelect = ({ name, label, unit, labelCols, rowClass }: {
  name: string; label: string; unit?: string; labelCols?: number; rowClass?: string;
}) => {
  const { s, phxDoc, onSearchField } = usePhx();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = phxCmsOptionList(phxDoc as PhxCmsDoc, name);
  const hasRow = rows.some((r) => String(r.id) === valStr);
  const selLabel = valStr ? phxCmsSelectedLabel(phxDoc as PhxCmsDoc, name, value as any) : '';
  /**
   * ★ `plate_spacing_code_id` is the one doc select dev gives an explicit `--`
   * placeholder, and it gives it UNCONDITIONALLY: renderSelects (js:1084-1086)
   * opens that field's markup with
   * `<option data-value="None" value="">--</option>` before the rows, and
   * changeModelField (js:2299-2303) re-prepends the same option whenever the
   * calculation mode is 1 (Recuperator's default). The port emitted the generic
   * empty-label blank, so Spacing read as an empty line where dev reads `--`.
   *
   * The OPTIONS were never mis-bound. The CMS doc's
   * `plate_heat_exchanger.spacing_code` rows really are
   * `{id, name: "U"|"X"|"R"|"S"|"N"|"C"|"M"|"L"|"W"|"F", value}` — spacing
   * CODES, one letter each — and dev prints the same list, through
   * `paramsData[item].series || paramsData[item].name` (js:1106-1109), which
   * falls through to `name` because these rows carry no `series`.
   */
  const spacingPlaceholder = name === 'plate_spacing_code_id';
  return (
    <SelectRow label={label} dataName={name} unit={unit} labelCols={labelCols} rowClass={rowClass}>
      <select name={name} data-name={name} className="search_field" value={valStr}
        onChange={(e) => onSearchField(name, e.target.value)}>
        {spacingPlaceholder
          ? <option value="" data-value="None">--</option>
          : valStr === '' && <option value="" />}
        {rows.map((r) => (
          <option key={String(r.id)} value={String(r.id)} data-value={String(r.id)}>
            {String(r.label ?? '')}
          </option>
        ))}
        {valStr !== '' && !hasRow && <option value={valStr}>{selLabel || valStr}</option>}
      </select>
    </SelectRow>
  );
};

// ----- master-list dependent select (temp/seal/plate/end_plate/corner/etc.) -----
const MasterSelect = ({ name, label, labelKey = 'name', unit, unitTemp, labelCols, rowClass }: {
  name: string; label: string; labelKey?: string; unit?: string; unitTemp?: boolean;
  labelCols?: number; rowClass?: string;
}) => {
  const { s, masterLists, onModelField } = usePhx();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = masterLists[name] ?? [];
  /**
   * ★ NO SYNTHETIC OPTION for a saved value the master list does not carry.
   *
   * dev's `renderSelects` (plate_heat_exchanger.selector.js:1080-1122) rebuilds
   * the select from the loaded list ALONE and then does `changeSelect.val(state)`.
   * jQuery's `.val()` against a list with no matching `<option>` leaves
   * `selectedIndex === -1`, so the control paints EMPTY while `self.state` keeps
   * the value — dev never invents a row for it and never prints a bare id.
   *
   * The port added a fallback `<option value={valStr}>` (labelled from the
   * model's `*_name` companion, else the raw id). It fires whenever the list is
   * empty, and one of these lists is empty by DATA on both boxes: End Plates →
   * Finish resolves through
   * `master/…/mfg/<m>/series/<s>/end_plate_mtl/<mtl>/end_plate_finishs/`
   * (`api/rest_master/urls_phx.py:95`), and
   * `ksdm_master_ahu_phx_mfg_series_mtl_eplate_fin` has ZERO rows in both
   * `ksdm_dev` and `ksdm_dev3` (its Internal Plates / Corner Profiles siblings
   * hold 18 and 12). The endpoint returns `[]`, `end_plate_finish_name` is not
   * on the serializer (`plate_exchanger_serializers.py:142-147` has the style,
   * mtl and corner-profile-finish companions but not this one), so the fallback
   * printed the raw id — the reported `Finish: 2` where dev shows blank.
   *
   * Dropping it is parity in every case the branch could fire: list empty, list
   * loaded without the value, or list not yet fetched — dev paints blank in all
   * three. `<select value="2">` with no matching option lands on
   * `selectedIndex === -1` exactly as jQuery's `.val()` does, and the value stays
   * in `state`, so save and the rate payload are untouched (the rate builder
   * already resolves-or-omits against the same list, js:1236-1248).
   */
  return (
    <SelectRow label={label} dataName={name} unit={unit} unitTemp={unitTemp}
      labelCols={labelCols} rowClass={rowClass}>
      <select name={name} data-name={name} className="model_field" value={valStr}
        onChange={(e) => onModelField(name, e.target.value)}>
        {valStr === '' && <option value="" />}
        {rows.map((r: any) => (
          <option key={String(r.id ?? r.value)} value={String(r.id ?? r.value)}>
            {optionLabel(r, labelKey)}
          </option>
        ))}
      </select>
    </SelectRow>
  );
};

const Num = ({ name, label, unit, formatter = 'decimal', model = true, readOnly, labelCols, rowClass, textRight, required }: {
  name: string; label: string; unit?: string; formatter?: 'integer' | 'decimal'; model?: boolean;
  readOnly?: boolean; labelCols?: number; rowClass?: string; textRight?: boolean; required?: boolean;
}) => {
  const { s, onModelField, onSearchField } = usePhx();
  return (
    <SelectRow label={label} dataName={name} unit={unit} labelCols={labelCols} rowClass={rowClass} required={required}>
      {/* legacy text inputs carry `padding-left-5px` in the properties/settings
          panes (tmpl:84, 545, 1097) — `.component-rating-mode input.padding-left-5px`
          (common.css:21000) is the only thing that insets the caret. */}
      <PhxNumberInput name={name} value={s[name]} formatter={formatter} autoComplete="off"
        className={`${model ? 'model_field' : 'search_field'}${textRight ? ' text-right' : ' padding-left-5px'}${readOnly ? ' non-clickable' : ''}`}
        readOnly={readOnly}
        onCommit={(raw) => (model ? onModelField(name, raw) : onSearchField(name, raw))} />
    </SelectRow>
  );
};
/**
 * A Performance row (tmpl:787-812). dev does NOT stack OA above RA — it puts the
 * pair side by side under two headings, one row per property:
 *
 *   col-xs-4 label-part | col-xs-8 input-part
 *                         └ row > col-xs-5 input-part-left no-left-padding  (supply)
 *                                 col-xs-2 no-right-padding padding-top-7   (unit)
 *                                 col-xs-5 no-right-padding                 (exhaust)
 *
 * The port had emitted two separate full-width rows labelled "OA …"/"RA …", which
 * is why the tab was twice as tall as dev's and half as wide.
 */
const PairNum = ({ base, sea, label, unit, unitTemp, formatter = 'decimal', rowClass }: {
  base: string; sea: SeasonTab; label: string; unit: string; unitTemp?: boolean;
  formatter?: 'integer' | 'decimal'; rowClass?: string;
}) => {
  const { s, onModelField } = usePhx();
  const oa = `oa_${base}_${sea}`;
  const ra = `ra_${base}_${sea}`;
  const cell = (name: string) => (
    <PhxNumberInput name={name} value={s[name]} formatter={formatter} autoComplete="off"
      className="text-right model_field" tabIndex={PERF_TAB_INDEX[side(name)]}
      onCommit={(raw) => onModelField(name, raw)} />
  );
  const uom = unitTemp ? 'uom temp right' : 'uom right';
  return (
    <div className={rowClass ? `row ${rowClass}` : 'row'}>
      <div className="col-xs-4 label-part">
        <span data-name={oa} data-name_2={ra}>{label}</span>
        <span className={unitTemp ? 'right temp uom' : 'right uom'}>{unit}</span>
      </div>
      <div className="col-xs-8 input-part">
        <div className="row">
          <div className="col-xs-5 input-part-left no-left-padding">{cell(oa)}</div>
          <div className="col-xs-2 no-right-padding padding-top-7">
            <span className={uom}>{unit}</span>
          </div>
          <div className="col-xs-5 no-right-padding">{cell(ra)}</div>
        </div>
      </div>
    </div>
  );
};

// ----- one season's stream block (oa/ra), WB(heatex)/RH(recuperator) toggled -----
const SeasonBlock = ({ sea }: { sea: SeasonTab }) => {
  const { s, season, isHeatex, streams, projectElevation, onModelField } = usePhx();
  const streamCell = (name: string) => (
    <select name={name} data-name={name} className="model_field" tabIndex={PERF_TAB_INDEX[side(name)]}
      value={s[name] == null ? '' : String(s[name])}
      onChange={(e) => onModelField(name, e.target.value)}>
      <option value="">- -</option>
      {streams.map((r) => (
        <option key={String(r.id)} value={String(r.id)}>{optionLabel(r, 'name')}</option>
      ))}
    </select>
  );
  return (
    // tmpl:744 — the season pane is `col-xs-12 col-lg-7 float-left`, i.e. it
    // occupies 7/12 of the tab, not the full width the port gave it.
    // `id` matches legacy (tmpl:744) and the EW selector's own season panes —
    // `common.css:20911` (`#rating_mode_plate_heat_exchanger_content #winter >
    // div:first-child`) needs it, and being scoped under the chassis id it stays
    // correct even with the EW popup's identically-named panes in the document.
    <div id={sea}
      className={`col-xs-12 col-lg-7 float-left ${sea} tab-pane fade${season === sea ? ' in active' : ''}`}>
      {/* the two column headings (tmpl:745-759) — never ported. */}
      <div className="row">
        <div className="col-xs-offset-4 col-xs-8 input-part">
          <div className="row subtitles">
            <div className="col-xs-5 output-data">
              <div className="row">
                <span className="output-without-unit left-padding-5">SUPPLY AIR ENTERING</span>
              </div>
            </div>
            <div className="col-xs-5 col-xs-offset-2 output-data">
              <div className="row output-part-right">
                <span className="output-without-unit left-padding-5">EXHAUST AIR ENTERING</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Air Stream (tmpl:760-786) — a select pair, so no middle unit cell; the
          right-hand cell carries the offset instead. */}
      <div className="row">
        <div className="col-xs-4 label-part" data-name={`oa_air_stream_${sea}_id`}
          data-name_2={`ra_air_stream_${sea}_id`}>
          Air Stream
        </div>
        <div className="col-xs-8 input-part">
          <div className="row">
            <div className="col-xs-5 input-part-left no-left-padding">
              {streamCell(`oa_air_stream_${sea}_id`)}
            </div>
            <div className="col-xs-5 col-xs-offset-2 no-right-padding">
              {streamCell(`ra_air_stream_${sea}_id`)}
            </div>
          </div>
        </div>
      </div>
      <PairNum base="airflow_actual" sea={sea} label="Airflow" unit="CFM" formatter="integer" />
      <PairNum base="db_temp" sea={sea} label="DB Temperature" unit="F" unitTemp />
      {/* RH for recuperator, WB for heatex — legacy ships both rows and toggles
          `.hide` onto `hide-for-heatex` / `show-for-heatex` (tmpl:834, 854). */}
      {!isHeatex
        ? <PairNum base="rh" sea={sea} label="Humidity" unit="%" rowClass="hide-for-heatex" />
        : (
          <>
            <PairNum base="wb_temp" sea={sea} label="WB Temperature" unit="F" unitTemp rowClass="show-for-heatex" />
            {/* ★ RESTORED: Pressure In (tmpl:874-892 winter / :1029-1046 summer),
                the second `show-for-heatex` row — the port had neither. */}
            <PairNum base="inlet_pressure" sea={sea} label="Pressure In" unit="in WC" rowClass="show-for-heatex" />
          </>
        )}
      {/* ★ RESTORED: Altitude (tmpl:893-907). Read-only, taken from the project's
          elevation and defaulting to 0, and supply-side only — there is no
          exhaust counterpart. Legacy renames the input `elevation` → `altitude`
          when it collects the rate payload (js:1639) and drops it entirely in
          header mode (getTreeOfAllInputs, js:2541-2546), so it stays display-only
          here too. */}
      <div className="row">
        <div className="col-xs-4 label-part">
          <span data-name="elevation">Altitude</span>
          <span className="right uom">ft</span>
        </div>
        <div className="col-xs-8 input-part">
          <div className="row">
            <div className="col-xs-5 input-part-left no-left-padding">
              <input type="text" name="elevation" data-name="altitude" readOnly tabIndex={-1}
                className="text-right" value={projectElevation} onChange={() => undefined} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- a plain fetched-options model select (module_configuration / size) -----
const OptionSelect = ({ name, label, options, unit, search, labelCols, selectClass }: {
  name: string; label: string; options: OptionRow[]; unit?: string; search?: boolean;
  labelCols?: number; selectClass?: string;
}) => {
  const { s, onSearchField, onModelField } = usePhx();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const hasRow = options.some((r) => String(r.id) === valStr);
  return (
    <SelectRow label={label} dataName={name} unit={unit} labelCols={labelCols}>
      <select name={name} data-name={name} className={selectClass ?? 'search_field'} value={valStr}
        onChange={(e) => (search ? onSearchField(name, e.target.value) : onModelField(name, e.target.value))}>
        <option value="">--</option>
        {options.map((r) => (
          <option key={String(r.id)} value={String(r.id)}>{optionLabel(r, 'name')}</option>
        ))}
        {valStr !== '' && !hasRow && <option value={valStr}>{valStr}</option>}
      </select>
    </SelectRow>
  );
};

// ----- series: an EDITABLE dependency select (downgrade fix — was a read-only
// <span>). Rows are `{id, series}` (no `name`), so the option text uses `series`
// FIRST (legacy plate_heat_exchanger.selector.js:1055/:1109). Cascade parent:
// onSearchField fires graph.onParentChange('series_id'). -----
const SeriesSelect = () => {
  const { s, masterLists, masterPhx, onSearchField } = usePhx();
  const rows = (masterLists.series_id ?? []) as Array<Record<string, any>>;
  const valStr = s.series_id == null || s.series_id === '' ? '' : String(s.series_id);
  const hasRow = rows.some((r) => String(r.id ?? r.value) === valStr);
  const savedLabel = String(masterPhx.series ?? valStr);
  // tmpl:73-89 — Series and Shape share ONE row: label `Series | Shape`, and an
  // input-part split `col-xs-9 phe-series-wrap` + `col-xs-3 phe-shape-wrap`.
  // Both cells are addressed by name in common.css:20869-20874
  // (`.phe-series-wrap select {width: calc(101% - 5px)}`, `.phe-shape-wrap
  // {left: calc(50% - 50px)}`) and :20866 (`#input_shape_id {width: calc(355%
  // - 5px); margin-left:-40px}`), so the port's bare full-width series select
  // was both the wrong width and missing Shape entirely.
  const shapeText = s.shape == null ? String(masterPhx.shape ?? '') : String(s.shape);
  return (
    <SplitRow
      label={
        <>
          <span data-name="series_id">Series</span>{' '}|{' '}
          <span data-name="shape_id">Shape</span>
        </>
      }
    >
      <div className="col-xs-9 no-left-padding phe-series-wrap">
        <select name="series_id" data-name="series" className="search_field" value={valStr}
          onChange={(e) => onSearchField('series_id', e.target.value)}>
          {valStr === '' && <option value="" />}
          {rows.map((r) => (
            <option key={String(r.id ?? r.value)} value={String(r.id ?? r.value)}>
              {optionLabel(r as OptionRow, 'series')}
            </option>
          ))}
          {valStr !== '' && !hasRow && <option value={valStr}>{savedLabel}</option>}
        </select>
      </div>
      <div className="col-xs-3 no-left-padding phe-shape-wrap">
        {/* legacy renders the readable shape in a readonly text box and keeps
            `#shape_id` as a `hidden search_field` the cascade writes to
            (tmpl:84-85); `shape_id` is already in SEARCH_FIELDS (:224). */}
        <input id="input_shape_id" name="shape_id" type="text" data-name="shape" readOnly
          tabIndex={-1} className="model_field padding-left-5px" value={shapeText} onChange={() => undefined} />
        <select id="shape_id" name="shape_id" data-name="shape" className="hidden search_field"
          value={s.shape_id == null ? '' : String(s.shape_id)} onChange={() => undefined}>
          <option value={s.shape_id == null ? '' : String(s.shape_id)} />
        </select>
      </div>
    </SplitRow>
  );
};

/**
 * `pricing_method_inputs.tmpl` verbatim — ONE row: a `col-xs-6 label-part`
 * reading "Heat-Ex | Freight" and a `col-xs-6 input-part four-input-row` holding
 * the two amounts side by side. The port rendered them as two stacked full-width
 * rows labelled "Heat-Ex : Mtl" and "Freight".
 */
const PricingPair = ({ cost }: { cost: boolean }) => {
  const { s, pmId, onModelField } = usePhx();
  const productName = cost ? 'cost_product_user' : 'price_product_user';
  const freightName = cost ? 'cost_freight_user' : 'price_freight_user';
  const money = (name: string, extra = '') => (
    <PhxNumberInput name={name} value={s[name]} formatter="decimal" autoComplete="off"
      dataMin={1} dataMax={1000000} dataStep={0.01} className={`model_field phx_field${extra}`}
      onCommit={(raw) => onModelField(name, raw)} />
  );
  return (
    <>
      {/* the trailing `{{pricing_method_id}}` is legacy's own (partial:1) — kept
          so any per-method rule still keys off it. */}
      <div className={`col-xs-6 label-part ${pmId ?? ''}`}>
        <span data-name={productName} data-name_2={freightName}>Heat-Ex | Freight</span>
        <span className="right uom">US$</span>
      </div>
      <div className="col-xs-6 input-part four-input-row">
        <div className="row">
          <div className="col-xs-6">{money(productName, ' row')}</div>
          <div className="col-xs-6">{money(freightName)}</div>
        </div>
      </div>
    </>
  );
};

// ----- certification multiCheckbox editor (downgrade fix). AVAILABLE list =
// masterLists.certification_id (dependency-loaded); SELECTED = selectedCerts.
// Board mode only (legacy js:939 — needs current_model.id). tmpl:1132-1158. -----
const CertificationEditor = () => {
  const { masterLists, selectedCerts, certListOpen, setCertListOpen, toggleCertification } = usePhx();
  const availableCerts = (masterLists.certification_id ?? []) as Array<Record<string, any>>;
  return (
  <li className="certification-group">
    <div>
      <button id="show_phx_certifications" type="button" className="icon-selection" tabIndex={-1}
        onClick={() => setCertListOpen((o) => !o)} />
      {/* expands via the `.active` class (common.css:20787 max-height 500px). */}
      <div className={`certification-edit-list${certListOpen ? ' active' : ''}`}>
        <ul>
          {availableCerts.map((c) => {
            const cid = Number(c.id ?? c.value);
            return (
              <li key={String(cid)}>
                <label>
                  <input type="checkbox" value={String(cid)} className="certification" name="certification"
                    checked={selectedCerts.has(cid)}
                    onChange={(e) => toggleCertification(cid, e.target.checked)} />
                  <span className="label">{optionLabel(c as OptionRow, 'name')}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
      {/* ★ One `<span>` PER selected certification, no separator text — that is what
          the legacy multiCheckbox writes (multi_checkbox_selector.js:112 concatenates
          "<span>" + outputSerializer(model) + "</span>"), and the value styling hangs
          off it: `.certification-group .checked-certifications span { font-size: 13px;
          color: #212121 }` (common.css:20749). The port emitted a bare text node via
          `.join(', ')`, so neither rule matched and the value inherited the row's font
          size and colour. The ", " between values is CSS too
          (`span:not(:empty) + span:not(:empty):before`, common.css:20753, with
          `margin-left: -0.25em`) — joining in JS both double-spaces it and keeps the
          spans from existing at all. */}
      <span className="checked-certifications output certification_output">
        {availableCerts
          .filter((c) => selectedCerts.has(Number(c.id ?? c.value)))
          .map((c) => [Number(c.id ?? c.value), optionLabel(c as OptionRow, 'name')] as const)
          .filter(([, name]) => Boolean(name))
          .map(([cid, name]) => <span key={String(cid)}>{name}</span>)}
      </span>
      <span className="label left">Certification</span>
    </div>
  </li>
  );
};

// ----- sub-tab nav (Properties + Bypass) -----
/**
 * ★ Each legacy `<li>` carries TWO classes: the pane id (`properties_more`,
 * `bypass_more`, …) and a `<name>-tab` (tmpl:31-44, :477-487). The port emitted
 * only the second, derived from its own React key — so General became
 * `general-tab` where legacy writes `properties_more properties-tab`. The
 * pane-id class is the one `changeSearchField`'s bypass branch targets
 * (js:2306-2316), so it has to be present and has to be the real name.
 *
 * ★ `locked` carries that branch's `.non-clickable`, which legacy puts on both
 * the `<li>` and its `<a>`.
 *
 * ★ The pane-id class is also the only thing in this block with GEOMETRY:
 * `.more_tabing > li.properties_more {padding-bottom:0 !important; height:21px;
 * line-height:21px}` (common.css:17741) against `.more_tabing > li
 * {padding-bottom:7px !important}` (:17735) for every other tab. Deriving the
 * class from the React key sat General 7px lower than its three siblings.
 * `hook` exists because the two navs cannot share a derived name — the
 * Properties nav's General tab opens `#properties_more` (tmpl:32) while
 * `general_more` belongs to the Bypass nav's first tab (tmpl:478).
 */
const SubTabNav = <T extends string>({ tabs, active, onPick, navClass, wrapCol = true, ulStyle }: {
  tabs: Array<{ key: T; paneId: string; label: string; hook?: string; pad?: string; locked?: boolean }>;
  active: T; onPick: (k: T) => void;
  navClass?: string; wrapCol?: boolean; ulStyle?: React.CSSProperties;
}) => {
  const ul = (
    <ul className="col-xs-12 text-right more_tabing" style={ulStyle}>
      {tabs.map((t, i) => (
        <Fragment key={t.key}>
          {/* THE WHITESPACE IS LOAD-BEARING. The template puts each `<li>` on its
              own line, and `.more_tabing > li {display:inline-block}` (:17735)
              means the newline between two of them renders as a real space — 4px
              at this font. JSX drops inter-element whitespace, so the port's strip
              came out 12px narrower than dev's across the three gaps and, because
              the ul is `text-right`, every tab sat left of where dev puts it. */}
          {i > 0 && ' '}
          <li className={`${t.paneId}${active === t.key ? ' active' : ''} ${t.hook ?? `${t.key}-tab`}${t.locked ? ' non-clickable' : ''}`}>
            {/* legacy puts `active` on the LI only (tmpl:32-45); every rule that
                colours the selected tab reads `li.active a` (:17760, :21074,
                :20907). No `active` on the anchor — but `.non-clickable` does go
                on both (js:2306-2316). */}
            {/* `pad` is the anchor's trailing whitespace, copied character for
                character from the template — a NON-breaking space, which is the
                point: a plain trailing space collapses at the end of a line box and
                would give none of the gap. The last tab of each nav has none
                (tmpl:44, :485). */}
            <a tabIndex={-1} className={t.locked ? 'non-clickable' : undefined}
              onClick={() => !t.locked && onPick(t.key)}>{t.label}{t.pad}</a>
          </li>
        </Fragment>
      ))}
    </ul>
  );
  return (
    <div className={`col-xs-12 sub-tab-menu nav nav-tabs${navClass ? ` ${navClass}` : ''}`}>
      {/* the Properties nav wraps its ul in an extra `.col-xs-12` (tmpl:30);
          the Bypass nav does not (tmpl:477). */}
      <div className="row">{wrapCol ? <div className="col-xs-12">{ul}</div> : ul}</div>
    </div>
  );
};

export default PlateHeatExchangerSelector;
